import {
  forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type DragEvent,
} from "react";
import { createPortal } from "react-dom";
import { addDays, format } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { can } from "../lib/permissions";
import { useEffectiveUser } from "../lib/auth";
import { useBetaDesign } from "../lib/platform";
import { ymd, isSameYmd } from "../lib/date";
import type { Assignment, CellNote, Employee, Shift, ShiftCode, TimeOff, TimeOffType } from "../lib/types";
import { TIME_OFF_CODE, TIME_OFF_LABEL } from "../lib/types";
import { compareBySeniority } from "../lib/rank";
import { hourOf, parseHourNote, bucketsBetween } from "../lib/hourBuckets";
import { Button } from "../components/ui/Button";
import { FixCoverageModal } from "../components/FixCoverageModal";
import { OnCallTodayStrip } from "../components/OnCallTodayStrip";
import { extendedHomeRange, shiftWrapsMidnight } from "../lib/coverage";
import { readableTextOn } from "../lib/colors";
import { applyTacOverlay, visibleShiftsAcross, isTacActiveOn, isShiftActiveOn, effectiveHomeShiftFor } from "../lib/tacMode";
import { buildEffectiveShiftLookup } from "../lib/shiftAssignments";
import { generateSchedulePdf, type ScheduleGroup } from "../lib/schedulePdf";

// 2-week pay-period schedule, grouped by shift.
//
// Interactions:
//   - Click any cell → inline text input for a code (SA, C, 9-4, XXX, …).
//     Enter saves. Esc cancels. Blank + Enter removes. Tab moves to next cell.
//   - Drag an employee's name row and drop it on another shift section's
//     header to change that employee's home shift.
//   - Drag a filled cell and drop it on another cell to move the
//     assignment (preserves shift + code, just changes the date).
//
// Each employee belongs to one section (their home shift). An assignment's
// shift defaults to that home shift; the `note` field holds the free text
// code displayed in the cell. Plain XXX (no custom code) displays as
// "XXX" automatically.

const DRAG_EMPLOYEE = "application/x-sm-employee";
const DRAG_ASSIGNMENT = "application/x-sm-assignment";

// Marker stamped into assignment.overrideReason when an L2+ supervisor
// force-saves a cell past a validation error. Kept as a stable
// prefix so old rows with additional context (" - <who> at <when>")
// still classify as forced. Used by:
//   * CellChip → renders the note in bold red when this tag is present
//   * CodePickerPopover → shows a "Revert override" button on top of
//     the usual "Remove code" when the existing row is forced
export const FORCE_OVERRIDE_TAG = "Force override — validation bypassed";
function isForcedOverride(a: Assignment | null | undefined): boolean {
  return !!a?.overrideReason?.startsWith(FORCE_OVERRIDE_TAG);
}

// (Cell-editing state lives at the page level as `picker`; cells just emit
// click events back up through onOpenPicker.)

// Compute the officer's WORKED hours label for a partial-time-off
// cell, mirroring how a supervisor would hand-type the reduced range
// into the cell note (e.g. shift 08:00-16:00 with vacation 13:00-16:00
// → "08-13"). The schedule convention is worked hours, not off hours,
// so the cell reads what the officer is actually on for. Middle-of-
// shift partial (off 10-12 out of 08-16) renders both worked segments
// comma-joined ("08-10,12-16"). Returns null if the shift can't be
// parsed or the off window fully covers the shift (nothing to work).
function partialWorkedLabel(
  shift: { startTime: string; durationMin: number },
  offStart: string, offEnd: string,
): string | null {
  const startH = hourOf(shift.startTime);
  if (startH === null) return null;
  const durationH = Math.round(shift.durationMin / 60);
  const shiftStartMin = startH * 60;
  const shiftEndMin = shiftStartMin + durationH * 60;
  const parseM = (s: string): number | null => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const offStartMin = parseM(offStart);
  const offEndMin = parseM(offEnd);
  if (offStartMin === null || offEndMin === null) return null;
  const cs = Math.max(offStartMin, shiftStartMin);
  const ce = Math.min(offEndMin, shiftEndMin);
  const fmt = (min: number): string => {
    const h = Math.floor(min / 60) % 24;
    const m = min % 60;
    if (m === 0) return String(h).padStart(2, "0");
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  // No overlap — off window is outside the shift entirely. Render
  // the whole shift as worked (no reduction).
  if (ce <= cs) {
    return `${fmt(shiftStartMin)}-${fmt(shiftEndMin)}`;
  }
  const parts: string[] = [];
  if (cs > shiftStartMin) parts.push(`${fmt(shiftStartMin)}-${fmt(cs)}`);
  if (ce < shiftEndMin)   parts.push(`${fmt(ce)}-${fmt(shiftEndMin)}`);
  return parts.length > 0 ? parts.join(",") : null;
}

export function Schedule() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const mayEditCells = can.editCells(me);
  const mayEditRoster = can.editRoster(me);
  const maySetAnchor = can.setScheduleAnchor(me);
  const mayViewAudit = can.viewAudit(me);
  const mayForceOverride = can.forceCellOverride(me);
  const nav = useNavigate();
  const { deptId } = useParams<{ deptId?: string }>();

  // The "fix coverage" modal — populated when a supervisor clicks a
  // red Coverage or OIC cell on a shift section. Also fired from an
  // OT × delete on the Section's OT row.
  const [fixCoverage, setFixCoverage] = useState<{ shiftId: string; date: string } | null>(null);
  // Coverage chip edit modal — opened on double-click of an OT
  // chip on the schedule. Lets the supervisor swap each covering
  // officer or wipe the chip entirely with notifications on both
  // paths.
  const [editingCoverage, setEditingCoverage] = useState<{
    rows: Assignment[]; shiftId: string; date: string;
  } | null>(null);
  // Scenario-only: id of the shift whose name / times are being
  // edited via the "Edit shift" banner button. The modal reads the
  // current scenario override (if any) layered on top of the live
  // shift row and writes back to schedule_scenarios.shift_overrides.
  const [editingScenarioShift, setEditingScenarioShift] = useState<string | null>(null);

  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const activeDept = useMemo(
    () => (departments.data ?? []).find((d) => d.id === deptId)
       ?? (departments.data ?? [])[0]
       ?? null,
    [departments.data, deptId],
  );

  // If no deptId was given in the URL but departments are loaded, redirect
  // to the first (default) department so the URL is stable.
  const needsRedirect = !deptId && activeDept !== null;

  // The shared "default" anchor lives on the agency row so every
  // user in every browser lands on the same pay period by default.
  // Owner / L1 can change that shared default via the date picker
  // (calls api.setScheduleAnchor). Every user — L1 through L4 — can
  // additionally scroll locally via Previous / Today / Next; those
  // updates stay in this browser only and don't touch the shared
  // anchor, so a junior supervisor browsing ahead doesn't jerk
  // everyone else's view.
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const serverAnchorStr = agency.data?.scheduleAnchorDate ?? null;
  const serverAnchor = useMemo(() => {
    if (serverAnchorStr) {
      const [y, m, dd] = serverAnchorStr.split("-").map(Number);
      if (y && m && dd) {
        const d = new Date(y, m - 1, dd);
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [serverAnchorStr]);

  // Null = follow the server's shared anchor; non-null = this user
  // has scrolled forward / back locally and is off the shared
  // default. Reset to null with the date picker (Owner/L1 only —
  // which also moves everyone else's shared default to that date).
  const [localAnchor, setLocalAnchor] = useState<Date | null>(null);
  const anchor = localAnchor ?? serverAnchor;

  // Per-device window width preference: 4 weeks (the pay-period
  // default) or 2 weeks (fits laptop / smaller displays without
  // horizontal scroll). Only 2 and 4 are accepted; anything else
  // falls back to 4. Persisted so the choice sticks across
  // reloads. The whole page — grid, Previous / Today / Next paging,
  // deep-link snap — reads scheduleDays instead of a raw 28 so
  // switching is a single-source change.
  const [scheduleWeeks, setScheduleWeeks] = useState<number>(() => {
    try {
      const raw = localStorage.getItem("schedule.weeks");
      const n = raw ? Number(raw) : NaN;
      return n === 2 ? 2 : 4;
    } catch { return 4; }
  });
  useEffect(() => {
    try { localStorage.setItem("schedule.weeks", String(scheduleWeeks)); }
    catch { /* ignore */ }
  }, [scheduleWeeks]);
  const scheduleDays = scheduleWeeks * 7;

  // iOS beta design: pins the schedule to a 7-day rolling window
  // (yesterday → today → next 5) instead of the standard 28-day
  // pay-period grid. Officer rows are condensed to initials and
  // radio numbers below. Owner-only and iOS-app-only — every
  // other surface keeps the 28-day view.
  const betaDesign = useBetaDesign();
  // Page offset in beta mode. 0 = the default "today's week"
  // window (yesterday + today + next 5). Swiping left bumps to
  // the next week (+1), swiping right to the previous week (-1).
  // Reset to 0 whenever the beta flag flips or the page mounts.
  const [betaWeekOffset, setBetaWeekOffset] = useState(0);
  useEffect(() => {
    setBetaWeekOffset(0);
  }, [betaDesign]);

  // On first mount (and on hard refresh), jump straight to the
  // page (2- or 4-week window per scheduleDays) that contains
  // today, regardless of where the shared agency anchor sits. The
  // shared anchor is typically the first day of a pay period
  // months in the past, so without this the schedule opens to
  // that ancient page and the supervisor has to hit "Today" every
  // time. Once they navigate with Previous / Next, the ref locks
  // the jump so it doesn't fire again until a remount (route
  // change away and back, or refresh).
  const didTodayJump = useRef(false);
  useEffect(() => {
    if (didTodayJump.current) return;
    if (!agency.data) return; // wait for the anchor to load
    didTodayJump.current = true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const msPerDay = 86_400_000;
    const diff = Math.floor((today.getTime() - serverAnchor.getTime()) / msPerDay);
    const pages = Math.floor(diff / scheduleDays);
    if (pages !== 0) setLocalAnchor(addDays(serverAnchor, pages * scheduleDays));
  }, [agency.data, serverAnchor, scheduleDays]);

  const setAnchorMut = useMutation({
    mutationFn: (date: Date) => api.setScheduleAnchor(ymd(date)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });
  // Date-picker set: Owner / L1 only, writes the shared anchor and
  // drops the local override so this browser follows the new
  // default too.
  function setSharedAnchor(d: Date) {
    if (!maySetAnchor) return;
    setLocalAnchor(null);
    setAnchorMut.mutate(d);
  }

  // Deep-link params for "→ cell" jumps from the Audit page. The
  // useEffect that acts on these lives further down, after
  // setSelection is declared — read them up here so both they and
  // setSearchParams stay in one place.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const anchorParam = searchParams.get("anchor");
  // Scenario mode: when ?scenario=<id> is set, this Schedule page
  // reads + writes the scenario shadow tables instead of the live
  // ones. The window clamps to the scenario's start/end dates and
  // a red banner replaces the normal top header controls.
  const scenarioParam = searchParams.get("scenario");
  const scenarioQ = useQuery({
    queryKey: ["scenario", scenarioParam],
    queryFn: () => scenarioParam ? api.getScenario(scenarioParam) : Promise.resolve(null),
    enabled: !!scenarioParam,
  });
  const scenario = scenarioQ.data ?? null;
  const scenarioMode = !!scenarioParam && !!scenario;
  const scenarioReadonly = scenarioMode && !!scenario?.publishedAt;
  const scenarioExcludedQ = useQuery({
    queryKey: ["scenarioExcluded", scenarioParam],
    queryFn: () => scenarioParam
      ? api.listScenarioExcluded(scenarioParam)
      : Promise.resolve([] as string[]),
    enabled: !!scenarioParam,
  });
  const scenarioExcluded = useMemo(
    () => new Set(scenarioExcludedQ.data ?? []),
    [scenarioExcludedQ.data],
  );

  const employees = useQuery({
    // Scenario mode doubles up on exclude + per-employee shift
    // placement overrides so a scenario add/remove invalidates
    // cleanly without a manual bust.
    queryKey: scenarioMode
      ? [
          "employees", activeDept?.id, "scenario", scenarioParam,
          JSON.stringify(scenario?.employeeShiftOverrides ?? {}),
          Array.from(scenarioExcluded).sort().join(","),
        ]
      : ["employees", activeDept?.id],
    queryFn: () => api.listEmployees(activeDept ? { departmentId: activeDept.id } : undefined),
    enabled: activeDept !== null,
    // Layer scenario shift placement overrides + exclusions on top
    // of the live employee rows. shiftLookup, roster grouping, and
    // the "+ Add officer" picker all read `shifts.data`-shape rows
    // so once we rewrite homeShiftId / drop excluded entries here,
    // the rest of the Schedule page just works.
    select: (data) => {
      if (!scenarioMode) return data;
      const overrides = scenario?.employeeShiftOverrides ?? {};
      return data
        .filter((e) => !scenarioExcluded.has(e.id))
        .map((e) => {
          if (!(e.id in overrides)) return e;
          const target = overrides[e.id];
          return { ...e, homeShiftId: target ?? null };
        });
    },
  });
  const shifts = useQuery({
    // Key doubles up on the scenario's shift_overrides so a rename /
    // time tweak invalidates the cache cleanly without needing a
    // manual invalidate call.
    queryKey: scenarioMode
      ? [
          "shifts", activeDept?.id, "scenario", scenarioParam,
          JSON.stringify(scenario?.shiftOverrides ?? {}),
        ]
      : ["shifts", activeDept?.id],
    queryFn: () => api.listShifts(activeDept ? { departmentId: activeDept.id } : undefined),
    enabled: activeDept !== null,
    // Scenario mode layers each shift's rename / time override from
    // schedule_scenarios.shift_overrides onto the live row before
    // anything downstream reads it. Every consumer of shifts.data
    // (the shift bar label, coverage math, extended-range chip
    // computation, …) picks the override up automatically.
    select: (data) => {
      if (!scenarioMode || !scenario?.shiftOverrides) return data;
      return data.map((s) => {
        const o = scenario.shiftOverrides![s.id];
        if (!o) return s;
        return {
          ...s,
          name: o.name ?? s.name,
          startTime: o.startTime ?? s.startTime,
          durationMin: o.durationMin ?? s.durationMin,
        };
      });
    },
    // Always refetch on mount so a toggle to a shift's settings
    // (requiresOic, minStaffing, etc.) on the Shifts page is
    // reflected the next time the supervisor opens this schedule —
    // no waiting for staleTime to expire.
    refetchOnMount: "always",
  });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const seniorityMode = useQuery({
    queryKey: ["seniorityMode"],
    queryFn: api.getSeniorityMode,
  });
  const payWeekendWeeks = useQuery({
    queryKey: ["payWeekendWeeks"],
    queryFn: api.getPayWeekendWeeks,
  });
  useEffect(() => {
    setPayWeekendWeeksForChrome(payWeekendWeeks.data ?? [0, 2]);
  }, [payWeekendWeeks.data]);

  // Tac-mode overlay. Non-destructive: filters shifts to the right
  // set for the current tac state and projects flagged officers
  // onto their tac home shift while tac mode is active. The raw
  // queries are still available downstream for anything that needs
  // unprojected data; everything that builds the grid layout reads
  // through the *Effective* aliases below.
  const days = useMemo(() => {
    // Scenario mode locks the window to the scenario's start → end
    // dates so the grid shows exactly what the mock covers (no
    // pay-period anchor logic, no beta 7-day rolling view).
    if (scenarioMode && scenario) {
      const start = new Date(scenario.startDate + "T00:00:00");
      const end = new Date(scenario.endDate + "T00:00:00");
      const out: Date[] = [];
      for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(new Date(d));
      return out;
    }
    if (betaDesign) {
      // Beta: 7-day rolling window. Offset 0 = yesterday + today
      // + next 5; offset 1 = the next week; offset -1 = the
      // previous week. Swipe handlers on the scroll container
      // mutate betaWeekOffset.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Array.from(
        { length: 7 },
        (_, i) => addDays(today, i - 1 + betaWeekOffset * 7),
      );
    }
    return Array.from({ length: scheduleDays }, (_, i) => addDays(anchor, i));
  }, [anchor, betaDesign, betaWeekOffset, scheduleDays, scenarioMode, scenario]);
  const startStr = ymd(days[0]);
  const endStr = ymd(days[days.length - 1]);
  // Date-aware tac overlay. effectiveShifts is the union of every
  // shift visible on ANY day of the window — for a page that
  // straddles tac mode's start date that's both shift families
  // simultaneously, and the per-cell sectionOwnsCell gate hides
  // the days each section doesn't own. effectiveEmployees is the
  // legacy projection (applyTacOverlay) used by downstream lookups
  // that don't yet support date-aware lookups (e.g. roster
  // grouping fallbacks); the per-cell rendering walks
  // effectiveHomeShiftFor for date-by-date precision.
  const dayStrs = useMemo(() => days.map((d) => ymd(d)), [days]);
  const tacOverlay = useMemo(
    () => applyTacOverlay(agency.data, shifts.data ?? [], employees.data ?? []),
    [agency.data, shifts.data, employees.data],
  );
  const effectiveShifts = useMemo(
    () => visibleShiftsAcross(agency.data, shifts.data ?? [], dayStrs),
    [agency.data, shifts.data, dayStrs],
  );
  const effectiveEmployees = tacOverlay.effectiveEmployees;
  const today = new Date();

  // Scenario mode replaces the live query with the shadow table.
  // The shadow rows have the same shape as live rows (the publish
  // RPC literally copies them), so every downstream reader keeps
  // working unchanged. We filter by the scenario's window
  // client-side rather than at the DB level so the queryKey stays
  // simple and keeps React Query's cache honest.
  const assignments = useQuery({
    queryKey: scenarioMode
      ? ["scenarioAssignments", scenarioParam, startStr, endStr]
      : ["assignments", startStr, endStr],
    queryFn: async () => {
      if (scenarioMode && scenarioParam) {
        const rows = await api.listScenarioAssignments(scenarioParam);
        return rows
          .filter((r) => r.date >= startStr && r.date <= endStr)
          .map((r) => ({
            id: r.id,
            agencyId: scenario?.agencyId ?? "",
            employeeId: r.employeeId,
            shiftId: r.shiftId,
            date: r.date,
            status: r.status as Assignment["status"],
            note: r.note,
            overrideReason: r.overrideReason,
            coverageGroupId: r.coverageGroupId,
            otStartTime: r.otStartTime,
            otEndTime: r.otEndTime,
            fromPattern: r.fromPattern,
            createdAt: r.createdAt,
          } as Assignment));
      }
      return api.listAssignments(startStr, endStr);
    },
  });

  // Approved time-off overlays: fetch the range once, pick
  // approved records, expand into a (empId|date) → code map used
  // by the home-cell renderer as a fall-through below assignments
  // and extRange but above empty. A denied or requested record is
  // intentionally ignored. Scenario mode swaps to the shadow
  // table.
  const timeOff = useQuery({
    queryKey: scenarioMode
      ? ["scenarioTimeOff", scenarioParam, startStr, endStr]
      : ["timeoff", startStr, endStr],
    queryFn: async () => {
      if (scenarioMode && scenarioParam) {
        const rows = await api.listScenarioTimeOff(scenarioParam);
        return rows
          .filter((r) => r.endDate >= startStr && r.startDate <= endStr)
          .map((r) => ({
            id: r.id,
            employeeId: r.employeeId,
            startDate: r.startDate,
            endDate: r.endDate,
            startTime: r.startTime,
            endTime: r.endTime,
            type: (r.type as TimeOffType),
            status: "approved",
            note: r.note,
            createdAt: r.createdAt,
            metadata: {},
            formSlug: null,
          } as unknown as TimeOff));
      }
      return api.listTimeOff(startStr, endStr);
    },
  });
  // Free-form supervisor notes attached to individual (employee,
  // date) cells. Rendered as chips at the top of the code-picker
  // popover.
  const cellNotes = useQuery({
    queryKey: ["cellNotes", startStr, endStr],
    queryFn: () => api.listCellNotes(startStr, endStr),
  });
  const cellNotesAt = useMemo(() => {
    const m = new Map<string, CellNote[]>();
    for (const n of cellNotes.data ?? []) {
      const key = `${n.employeeId}|${n.date}`;
      const list = m.get(key) ?? [];
      list.push(n);
      m.set(key, list);
    }
    return m;
  }, [cellNotes.data]);
  const invalidateCellNotes = () =>
    qc.invalidateQueries({ queryKey: ["cellNotes"] });
  const addCellNoteMut = useMutation({
    mutationFn: (args: { employeeId: string; date: string; note: string }) =>
      api.addCellNote(args.employeeId, args.date, args.note),
    onSuccess: invalidateCellNotes,
  });
  const deleteCellNoteMut = useMutation({
    mutationFn: (id: string) => api.deleteCellNote(id),
    onSuccess: invalidateCellNotes,
  });
  // Map of (employeeId|date) → time-off overlay data. `code` is the
  // status letter to render (V/PL/S…); startTime / endTime are set
  // only for partial-day requests and drive the amber overlay +
  // hover tooltip on cells whose assignment happens to overlap the
  // partial window. Whole-day rows keep the historical "hide the
  // assignment, show the code chip" behaviour.
  const timeOffAt = useMemo(() => {
    const m = new Map<string, {
      code: string;
      typeLabel: string;
      startTime: string | null;
      endTime: string | null;
      note: string | null;
    }>();
    for (const t of timeOff.data ?? []) {
      if (t.status !== "approved") continue;
      const start = new Date(t.startDate + "T00:00:00");
      const end = new Date(t.endDate + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        m.set(`${t.employeeId}|${ymd(d)}`, {
          code: TIME_OFF_CODE[t.type],
          typeLabel: TIME_OFF_LABEL[t.type] ?? t.type,
          startTime: t.startTime,
          endTime: t.endTime,
          note: t.note,
        });
      }
    }
    return m;
  }, [timeOff.data]);

  // IOD periods for the visible window. Materialiser already writes
  // IOD assignment rows for shift-assigned officers; this overlay is
  // for UNASSIGNED officers (no home / tac home) — their row in the
  // grid has no shift to write rows against, so the cells fall back
  // to this map to render the IOD chip when a period covers the
  // date. Pure visual overlay; coverage doesn't include unassigned
  // officers anyway so no double-counting concerns.
  const iodPeriods = useQuery({
    queryKey: ["iodPeriods", startStr, endStr],
    queryFn: () => api.listIodPeriods(startStr, endStr),
  });
  const iodOverlayAt = useMemo(() => {
    const m = new Map<string, true>();
    for (const p of iodPeriods.data ?? []) {
      let cur = p.startDate > startStr ? p.startDate : startStr;
      const end = p.endDate && p.endDate < endStr ? p.endDate : endStr;
      while (cur <= end) {
        m.set(`${p.employeeId}|${cur}`, true);
        const d = new Date(cur + "T00:00:00");
        d.setDate(d.getDate() + 1);
        cur = d.toISOString().slice(0, 10);
      }
    }
    return m;
  }, [iodPeriods.data, startStr, endStr]);

  // Days-off pattern overlay for UNASSIGNED officers. Once they're
  // moved onto a shift (homeShiftId or tacHomeShiftId set), the
  // pattern is materialised onto real assignment rows by
  // applyDaysOffPattern and this overlay no longer applies. While
  // they're unassigned, the schedule grid still wants to show
  // their XXX cells so the supervisor sees the planned days off —
  // we compute them on the fly from the same weekday set the
  // materialiser would have written.
  const patternOverlayAt = useMemo(() => {
    const m = new Map<string, true>();
    for (const e of employees.data ?? []) {
      if (e.homeShiftId || e.tacHomeShiftId) continue; // shift-assigned → covered by materialiser
      if (!e.daysOffStartDate) continue;
      const isTac = e.daysOffMode === "tac";
      const w1 = (isTac ? e.tacDaysOffWeekdays : e.daysOffWeekdays) ?? [];
      const w2 = isTac ? e.tacDaysOffWeekdaysW2 : e.daysOffWeekdaysW2;
      if (w1.length === 0) continue;
      const w1Set = new Set(w1);
      const w2Set = w2 && w2.length > 0 ? new Set(w2) : null;
      const startMs = new Date(e.daysOffStartDate + "T00:00:00").getTime();
      for (const d of days) {
        const dStr = ymd(d);
        if (dStr < e.daysOffStartDate) continue;
        if (e.retirementDate && dStr >= e.retirementDate) continue;
        const wd = d.getDay();
        let activeSet: Set<number> = w1Set;
        if (w2Set) {
          const daysSince = Math.floor((d.getTime() - startMs) / 86_400_000);
          activeSet = Math.floor(daysSince / 7) % 2 === 0 ? w1Set : w2Set;
        }
        if (activeSet.has(wd)) {
          m.set(`${e.id}|${dStr}`, true);
        }
      }
    }
    return m;
  }, [employees.data, days]);

  const coverage = useQuery({
    // Department id is part of the key so switching schedules
    // doesn't serve the previous department's cached coverage. The
    // employees / shifts queries upstream already key on deptId; the
    // coverage query was the odd one out, which is why a click from
    // Patrol → Dispatch (or vice versa) showed stale or empty
    // chips until the next refetch. Scenario mode skips the
    // coverage math entirely (returns an empty list) — the mock
    // schedule shows the grid without coverage / OIC chips to make
    // "this is a draft" obvious, and scenario coverage math isn't
    // computed server-side yet.
    queryKey: scenarioMode
      ? ["scenarioCoverage", scenarioParam, startStr, endStr]
      : ["coverage", activeDept?.id, startStr, endStr],
    queryFn: () => scenarioMode
      ? Promise.resolve([] as Awaited<ReturnType<typeof api.coverage>>)
      : api.coverage(startStr, endStr, {
      departmentId: activeDept?.id,
      // Reuse whatever the page's other useQuery hooks have already
      // fetched — api.coverage internally needs shifts, employees,
      // agency, assignments, codes, and time-off, all of which the
      // Schedule page has separate queries for. Handing them in
      // eliminates the equivalent Supabase round-trips inside
      // api.coverage (previously 7 sequential, then 7 parallel; now
      // typically just 1: coverage_overrides). The employees query
      // above is dept-filtered so we pull the all-employees list
      // from React Query cache (populated by Layout's own query)
      // via qc.getQueryData; without the full list, cross-dept OT
      // wouldn't count in coverage.
      prefetched: {
        shifts: shifts.data,
        employees: qc.getQueryData(["employees"]),
        agency: agency.data,
        assignments: assignments.data,
        codes: shiftCodes.data,
        timeOff: timeOff.data,
      },
    }),
    // Wait for the upstream queries to have data — otherwise
    // api.coverage falls back to fetching them itself, which is
    // slower.
    enabled: activeDept !== null
      && !!shifts.data && !!agency.data && !!assignments.data
      && !!shiftCodes.data && !!timeOff.data,
    // Same reason as the shifts query above: opening a schedule
    // recomputes coverage so freshly-edited shift / employee /
    // requires-OIC settings show through immediately.
    refetchOnMount: "always",
  });

  // Temporary shift placements that override homeShiftId for a
  // window. Fetched for the visible 4-week range so the grid can
  // surface an officer under whichever shift they're actually on
  // each day. Range filter on the query side keeps it cheap.
  const shiftAssignments = useQuery({
    queryKey: ["shiftAssignments", startStr, endStr],
    queryFn: () => api.listShiftAssignments(startStr, endStr),
  });
  const shiftLookup = useMemo(
    () => buildEffectiveShiftLookup(
      // Use RAW employees here, not effectiveEmployees — the lookup
      // wants the unprojected homeShiftId / tacHomeShiftId pair so
      // it can pick per date based on the tac transition. Passing
      // the already-projected list would collapse the two values
      // and lose the regular-then-tac transition on a straddling
      // window.
      employees.data ?? [],
      shiftAssignments.data ?? [],
      agency.data,
      dayStrs,
    ),
    [employees.data, shiftAssignments.data, agency.data, dayStrs],
  );

  const coverageByShiftDate = useMemo(() => {
    const m = new Map<string, {
      assigned: number; minStaffing: number;
      oicAssigned: number; oicRequired: number;
      overridden: boolean;
    }>();
    for (const c of coverage.data ?? []) {
      m.set(`${c.shiftId}|${c.date}`, {
        assigned: c.assigned,
        minStaffing: c.minStaffing,
        oicAssigned: c.oicAssigned,
        oicRequired: c.oicRequired,
        overridden: c.overridden,
      });
    }
    return m;
  }, [coverage.data]);

  // Home-shift cell map: for each (employee, date) pair, pick the
  // assignment we should render in the employee's row. Prefer one
  // that matches their current home shift; fall back to any
  // assignment that day so a stale off-code (e.g. XXX on a shift the
  // employee no longer belongs to) still surfaces in the UI instead
  // of silently disappearing. Cross-shift *overtime* rows are kept in
  // otByShiftDate below, not here.
  const employeesById = useMemo(
    () => new Map(effectiveEmployees.map((e) => [e.id, e])),
    [effectiveEmployees],
  );
  const shiftCodeByCode = useMemo(
    () => new Map((shiftCodes.data ?? []).map((c) => [c.code.toUpperCase(), c])),
    [shiftCodes.data],
  );
  const assignmentAt = useMemo(() => {
    const m = new Map<string, Assignment>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId !== a.shiftId) continue;
      m.set(`${a.employeeId}|${a.date}`, a);
    }
    return m;
  }, [assignments.data, employeesById]);

  // Fallback for stale cross-shift rows whose code isn't OT (e.g. an
  // XXX left behind after an employee's home_shift_id changed). We
  // still want to surface them in the employee's home cell so they
  // don't silently disappear.
  //
  // OT / OT½ rows are intentionally excluded — those are already
  // represented by the consolidated OT row + the extended-hours
  // chip on the covering officer's home cell. Letting them through
  // here caused a literal "OT½" text chip to render on a covering
  // officer's home cell when the extended-hours chip lived on a
  // different date (first-half Night→Day covers put the chip on
  // the previous calendar day).
  const crossAssignmentAt = useMemo(() => {
    const m = new Map<string, Assignment>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId === a.shiftId) continue;
      const noteUpper = (a.note ?? "").trim().toUpperCase();
      if (noteUpper === "OT" || noteUpper === "OT½" || noteUpper === "OT/2") continue;
      const key = `${a.employeeId}|${a.date}`;
      if (!m.has(key)) m.set(key, a);
    }
    return m;
  }, [assignments.data, employeesById]);

  // Cross-shift OT rows: only count an assignment as overtime when
  // its code resolves to a positive coverage contribution. A stale
  // off-code (XXX, S, V…) on the "wrong" shift isn't overtime — it's
  // an orphan that should render on the employee's home row (above),
  // not as a fake OT helper on some other shift.
  const otByShiftDate = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId === a.shiftId) continue;
      const note = (a.note ?? "").trim().toUpperCase();
      const cov = note ? shiftCodeByCode.get(note)?.coverageValue : undefined;
      // Blank note defaults to 1.0 (someone showing up to work);
      // known code must have coverage > 0 to count; unknown text
      // (custom string) is treated as working.
      const contributing = !note || cov === undefined || cov > 0;
      if (!contributing) continue;
      const key = `${a.shiftId}|${a.date}`;
      const bucket = m.get(key);
      if (bucket) bucket.push(a);
      else m.set(key, [a]);
    }
    return m;
  }, [assignments.data, employeesById, shiftCodeByCode]);

  const shiftById = useMemo(
    () => Object.fromEntries((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );

  // For each (employee, date) where the employee is doing OT on a
  // neighboring shift, compute the extended worked range to display
  // in their HOME-shift cell. Format: "21-11" (24h, no minutes).
  //
  // Also builds a parallel map from chip cell → the underlying OT
  // assignments so the clear-cell path can delete them even when
  // the chip lives on a different calendar day than the OT row
  // (first-half wrap-midnight covers).
  const { extendedRangeAt, extendedRangeAssignmentsAt } = useMemo(() => {
    const rangeMap = new Map<string, string>();
    const assignMap = new Map<string, Assignment[]>();
    // RAW employee lookup — the projected employeesById from above
    // applies the agency-wide tac overlay, which means a
    // follows-tac officer's homeShiftId reads as their
    // tacHomeShiftId on every date. That breaks the chip helper on
    // pre-transition dates: the "home shift" the helper looks for
    // (B Set, etc.) isn't in the regular-pool, so adjacentShifts
    // can't find the officer's row in rotation and the chip
    // silently drops.
    const rawById = new Map((employees.data ?? []).map((e) => [e.id, e]));
    for (const a of assignments.data ?? []) {
      const emp = rawById.get(a.employeeId);
      if (!emp) continue;
      // Per-date effective home, so an officer flagged
      // followsTacMode lands on Nights for pre-transition cover
      // and B Set for post-transition cover. Skip when that home
      // matches the OT shift — nothing to chip.
      const effectiveHomeId = effectiveHomeShiftFor(agency.data, emp, a.date);
      if (!effectiveHomeId || effectiveHomeId === a.shiftId) continue;
      const note = (a.note ?? "").trim().toUpperCase();
      const cov = note ? shiftCodeByCode.get(note)?.coverageValue : undefined;
      const contributing = !note || cov === undefined || cov > 0;
      if (!contributing) continue;
      const homeShift = (shifts.data ?? []).find((s) => s.id === effectiveHomeId);
      const otShift = (shifts.data ?? []).find((s) => s.id === a.shiftId);
      if (!homeShift || !otShift) continue;
      // Adjacency / chip-date helpers below need the shift family
      // that's actually in rotation on the OT's date — NOT the
      // window-wide union. A page that straddles tac mode would
      // otherwise drag tac shifts into Nights' adjacency pool and
      // make next = "A Set" instead of wrapping to Day, dropping
      // the extended-hours chip silently or placing it on the
      // wrong calendar day for officers whose home shift wraps
      // midnight.
      const visibleShiftList = isTacActiveOn(agency.data, a.date)
        ? (shifts.data ?? []).filter((s) => s.tacRole !== "regular")
        : (shifts.data ?? []).filter((s) => s.tacRole !== "tac");
      // Prefer the explicit hours stored on the OT assignment (from
      // the Fix Coverage window picker or a custom entry). Fall back
      // to the computed half/full range if the assignment predates
      // the window feature.
      let range: string | null;
      if (a.otStartTime && a.otEndTime) {
        range = `${Number(a.otStartTime.split(":")[0])}-${Number(a.otEndTime.split(":")[0])}`;
      } else {
        range = extendedHomeRange(homeShift, otShift, a, visibleShiftList);
      }
      if (!range) continue;
      // Chip placement — decides which calendar date the "6-18" (or
      // "21-9", etc.) label lands on in the officer's home row.
      //
      // Keyed off the coverage ROLE (from the Fix Coverage modal,
      // recorded in overrideReason) instead of the OT hours or the
      // adjacency alone. Role tells us unambiguously what kind of
      // cover this is:
      //
      //   * "full coverage" / "cross-department" / anything else
      //     → chip on a.date. Full covers are always on the OT's
      //       own date; the officer isn't extending a home shift,
      //       they're just working someone else's shift outright.
      //       This is what fixes Colt (V on 8/1, cover A Set 6-18
      //       on 8/2 → chip on 8/2, not 8/1) and Wagner (Night
      //       officer covering Evening 18-06 on 8/2 → chip on 8/2).
      //
      //   * "covering first half" — the officer's home shift is
      //     BEFORE the gap in the rotation. If their home shift
      //     wraps midnight, that home-shift instance started the
      //     day before the OT's calendar day (Night 22-08 8/3
      //     bleeds into a Day 8/4 first-half cover; the chip
      //     belongs on 8/3 where the Night row lives). Otherwise
      //     home is same-day (Evening 14-00 covering first-half
      //     of Night 22-08 8/4 → Evening row is on 8/4 → chip on 8/4).
      //
      //   * "covering second half" — the officer's home shift is
      //     AFTER the gap. If the GAP wraps midnight, the gap's
      //     second half sits on the NEXT calendar day (Day 06-18
      //     8/5 covering second-half of Night 22-08 8/4 → Day row
      //     is on 8/5 → chip on 8/5). Otherwise the officer's
      //     home is same-day as the gap (Evening 14-00 covering
      //     second-half of Day 06-18 8/4 → chip on 8/4).
      const role = (a.overrideReason ?? "").toLowerCase();
      const isFirstHalf = role.includes("first half");
      const isSecondHalf = role.includes("second half");
      let chipDate = a.date;
      if (isFirstHalf && shiftWrapsMidnight(homeShift)) {
        chipDate = shiftYmd(a.date, -1);
      } else if (isSecondHalf && shiftWrapsMidnight(otShift)) {
        chipDate = shiftYmd(a.date, +1);
      }
      const key = `${emp.id}|${chipDate}`;
      rangeMap.set(key, range);
      const bucket = assignMap.get(key);
      if (bucket) bucket.push(a);
      else assignMap.set(key, [a]);
    }
    return {
      extendedRangeAt: rangeMap,
      extendedRangeAssignmentsAt: assignMap,
    };
  }, [assignments.data, employees.data, shiftCodeByCode, shifts.data, agency.data]);

  // ---- Mutations -------------------------------------------------------
  const invalidate = () => {
    if (scenarioMode) {
      qc.invalidateQueries({ queryKey: ["scenarioAssignments", scenarioParam] });
      qc.invalidateQueries({ queryKey: ["scenarioTimeOff", scenarioParam] });
      qc.invalidateQueries({ queryKey: ["scenarioExcluded", scenarioParam] });
    } else {
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["timeoff"] });
    }
  };

  // Scenario-aware wrappers for the live create / delete calls.
  // In scenario mode the writes go to scenario_assignments; otherwise
  // they go to the live assignments table unchanged.
  async function createAssignmentCompat(input: {
    employeeId: string; shiftId: string; date: string;
    note?: string; overrideReason?: string; coverageGroupId?: string;
    otStartTime?: string; otEndTime?: string; fromPattern?: boolean;
  }) {
    if (scenarioMode && scenarioParam) {
      await api.upsertScenarioAssignment({
        scenarioId: scenarioParam,
        employeeId: input.employeeId,
        shiftId: input.shiftId,
        date: input.date,
        note: input.note ?? null,
        otStartTime: input.otStartTime ?? null,
        otEndTime: input.otEndTime ?? null,
      });
      return;
    }
    await api.createAssignment(input);
  }
  async function deleteAssignmentCompat(id: string) {
    if (scenarioMode && scenarioParam) {
      await api.deleteScenarioAssignment(id);
      return;
    }
    await api.deleteAssignment(id);
  }

  const upsertMut = useMutation({
    mutationFn: async (args: {
      employeeId: string;
      date: string;
      shiftId: string;
      code: string;               // "" means: remove
      existing: Assignment | null;
      // True when an L2+ supervisor bypassed the normal validation
      // (OT-elsewhere, etc.) via "Force save anyway" in the picker.
      // Stored in overrideReason with the FORCE_OVERRIDE_TAG so the
      // cell renders bold red and "Revert override" can find it.
      forced?: boolean;
    }) => {
      if (args.existing) await deleteAssignmentCompat(args.existing.id);
      if (!args.code && !args.existing) return;
      if (!args.code) return; // removed, nothing more to do
      const overrideReason = args.forced
        ? FORCE_OVERRIDE_TAG
        : args.existing ? "Changed on schedule grid" : undefined;
      await createAssignmentCompat({
        employeeId: args.employeeId,
        date: args.date,
        shiftId: args.shiftId,
        note: args.code,
        overrideReason,
      });
    },
    onSuccess: invalidate,
    onError: (e: { message?: string } | Error) => {
      setFlashError(
        e && "message" in e && typeof e.message === "string" && e.message
          ? e.message
          : "Couldn't save that change.",
      );
    },
  });

  const moveAssignmentMut = useMutation({
    mutationFn: async (args: {
      existingId: string; employeeId: string; shiftId: string; note: string | null;
      toDate: string;
    }) => {
      await deleteAssignmentCompat(args.existingId);
      await createAssignmentCompat({
        employeeId: args.employeeId,
        date: args.toDate,
        shiftId: args.shiftId,
        note: args.note ?? undefined,
        overrideReason: "Moved on schedule grid",
      });
    },
    onSuccess: invalidate,
  });

  // Every "move officer between shifts" path runs through this
  // helper so the days-off / IOD materialiser stays in sync with
  // the new state. Without this, the × and + buttons left stale
  // from_pattern XXX rows on the old shift (which then surfaced
  // as orphan chips on the grid) and missed materialising the
  // pattern onto a freshly-added shift. The applyDaysOffPattern
  // helper already handles the three cases internally:
  //   * new target → delete stale on old shift, insert on new
  //   * unchanged target → noop on inserts, only cleans drift
  //   * null target → delete every from_pattern row in window
  const moveOfficerMut = useMutation({
    mutationFn: async (
      { id, patch }: { id: string; patch: Partial<Employee> },
    ) => {
      const saved = await api.updateEmployee(id, patch);
      const isTac = saved.daysOffMode === "tac";
      const target = isTac
        ? (saved.tacHomeShiftId ?? saved.homeShiftId)
        : (saved.homeShiftId ?? saved.tacHomeShiftId);
      const w1 = (isTac ? saved.tacDaysOffWeekdays : saved.daysOffWeekdays) ?? [];
      const w2 = isTac ? saved.tacDaysOffWeekdaysW2 : saved.daysOffWeekdaysW2;
      const sd = saved.daysOffStartDate;
      if (sd) {
        await api.applyDaysOffPattern({
          employeeId: id, week1: w1, week2: w2, startDate: sd,
          homeShiftId: target, silent: true,
        });
      }
      return saved;
    },
    onSuccess: invalidate,
  });

  // Used by the OT row's × button on a Section to remove an
  // overtime-coverage assignment.
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAssignmentCompat(id),
    onSuccess: invalidate,
  });

  // (Previously kept a removeTimeOffDateMut here that trimmed /
  // deleted an approved time_off row from the schedule cell — now
  // gone. The schedule grid never mutates time_off records; to
  // cancel an approved request, use the Time Off page. See the
  // note near applyCodeToSelection for the reasoning.)

  // ---- Selection + picker state ---------------------------------------
  // A cell key is "employeeId|date". The anchor is the last plain-clicked
  // cell and is used for Shift-click range selection.
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // Deep-link consumer for "→ cell" jumps from the Audit page and
  // clicks on dashboard coverage widgets.
  //   ?focus=<employeeId>|<YYYY-MM-DD>    → select + scroll to cell
  //   ?anchor=<YYYY-MM-DD>                → shift the pay-period
  //                                         window locally so the
  //                                         focused date is in view
  // Both params are consumed once — we clear them off the URL after
  // acting so a page refresh or subsequent navigation doesn't keep
  // re-scrolling the grid.
  //
  // `anchor` snaps to the pay-period PAGE (2 or 4 weeks per the
  // user's scheduleDays setting) that contains the target date
  // rather than pinning the target to column 0. Same math the
  // "jump to today" effect above uses: floor the day-diff from
  // the shared server anchor into scheduleDays-sized chunks.
  // Preserves pay-period alignment so a jump to 10-06 opens the
  // enclosing page with 10-06 sitting in its natural column,
  // rather than restarting the window at 10-06 and straddling
  // two pay periods.
  useEffect(() => {
    if (!focusParam && !anchorParam) return;
    // Snap requires the shared server anchor to know where the
    // pay-period boundaries sit. Wait a tick if it's not loaded
    // yet — the effect re-runs with the same params below.
    if (anchorParam && !agency.data) return;
    if (anchorParam && /^\d{4}-\d{2}-\d{2}$/.test(anchorParam)) {
      const [y, m, d] = anchorParam.split("-").map(Number);
      const target = new Date(y, m - 1, d);
      target.setHours(0, 0, 0, 0);
      const msPerDay = 86_400_000;
      const diff = Math.floor((target.getTime() - serverAnchor.getTime()) / msPerDay);
      const pages = Math.floor(diff / scheduleDays);
      const pageStart = addDays(serverAnchor, pages * scheduleDays);
      setLocalAnchor(pageStart);
      // Also pulse the whole target-date column with a solid amber
      // background wash so the eye lands on the day even without a
      // per-cell ?focus. `cell-column-pulse` fills each cell with
      // amber that fades to transparent (see styles.css); using a
      // fill instead of the per-cell inset ring keeps the column
      // reading as one continuous highlighted strip. Every cell in
      // the schedule grid carries data-cell-key = "empId|YYYY-MM-
      // DD", so a suffix selector grabs every cell in the date's
      // column across every officer row. Two RAFs so the new
      // anchor renders first (the cells for pageStart..pageStart+27
      // need to exist before we query for them).
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const cells = document.querySelectorAll<HTMLElement>(
          `[data-cell-key$="|${CSS.escape(anchorParam)}"]`,
        );
        cells.forEach((el) => {
          el.classList.remove("cell-column-pulse");
          void el.offsetWidth;
          el.classList.add("cell-column-pulse");
          setTimeout(() => el.classList.remove("cell-column-pulse"), 2600);
        });
      }));
    }
    if (focusParam && focusParam.includes("|")) {
      setSelection(new Set([focusParam]));
      // Two RAFs so both the anchor change and the roster query
      // have a tick to settle before we look up the cell's DOM node.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(
          `[data-cell-key="${CSS.escape(focusParam)}"]`,
        );
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        // Amber pulse (see @keyframes cellFocusPulse in styles.css)
        // that fades over ~2.5s so the eye can land on the cell
        // before the highlight clears. Restart the animation by
        // removing + reflowing + re-adding the class in case the
        // same cell is focused twice in a row via back/forward.
        el.classList.remove("cell-focus-pulse");
        void el.offsetWidth;
        el.classList.add("cell-focus-pulse");
        setTimeout(() => el.classList.remove("cell-focus-pulse"), 2600);
      }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    next.delete("anchor");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusParam, anchorParam, agency.data]);

  // Shift sections a supervisor has collapsed on this visit — click
  // the shift's colored header bar to fold everything under it out
  // of sight. In-session only; resets on reload.
  const [collapsedShifts, setCollapsedShifts] = useState<Set<string>>(new Set());
  // Column-hover highlight: tracks the 0..27 dayIndex currently
  // under the cursor, so every cell in the same column lights up
  // across every row. Null when the cursor leaves the table.
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  // Row-hover companion: the employee id for the row under the
  // cursor. Combined with hoveredCol, the two paint a crosshair so
  // a supervisor scanning a long row keeps their place when reading
  // off a date — and reads back which officer lands in any given
  // column. Reset together with hoveredCol on mouseleave.
  const [hoveredEmpId, setHoveredEmpId] = useState<string | null>(null);
  const HOVER_COL_STYLE = { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" };
  function toggleShiftCollapsed(shiftId: string) {
    setCollapsedShifts((prev) => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId); else next.add(shiftId);
      return next;
    });
  }
  const [selAnchor, setSelAnchor] = useState<{ employeeId: string; date: string } | null>(null);
  const [picker, setPicker] = useState<{
    employee: Employee;
    date: string;
    anchor: DOMRect;
  } | null>(null);
  // Inline cell editor — a text input that overlays a single cell
  // with a typeahead dropdown of shift codes. Used for the common
  // case of "click a cell and type", separate from the picker
  // popover which still drives bulk-edits across a multi-cell
  // selection.
  const [inlineEdit, setInlineEdit] = useState<{
    employee: Employee;
    date: string;
    anchor: DOMRect;
    seed: string;
  } | null>(null);
  const [clipboardCode, setClipboardCode] = useState<string | null>(null);
  // Transient error banner (e.g. blocked off-code on a cell where
  // the officer is already covering OT somewhere else that day).
  const [flashError, setFlashError] = useState<string | null>(null);

  // Soft coverage-suggestion banner. Fires when a supervisor types
  // an hour-range note into an officer's home cell whose hours
  // extend past the home shift's window. The overflow portion
  // suggests recording OT on the adjacent shift so it actually
  // shows up as coverage there instead of being lost as an
  // unnoticed custom-hours note. Dismissed when the supervisor
  // clicks Yes/No or edits a different cell.
  const [coverageSuggestion, setCoverageSuggestion] = useState<{
    employeeId: string;
    employeeLabel: string;
    date: string;
    homeShift: Shift;
    adjacentShift: Shift;
    otStart: string;   // HH:00
    otEnd: string;     // HH:00
    typedNote: string; // the hour-range the supervisor typed
  } | null>(null);

  // Ordered roster used to compute Shift-click ranges. Must mirror the
  // render order below (we recompute `groups` later). We build a flat
  // list of employeeIds here once the data loads and memoise.
  const employeeOrder = useMemo(() => {
    if (!employees.data || !shifts.data) return [] as string[];
    const active = effectiveEmployees.filter((e) => e.status !== "inactive");
    const shiftOrder = effectiveShifts.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const byId: Record<string, Employee[]> = {};
    const unassigned: Employee[] = [];
    for (const e of active) {
      if (e.homeShiftId && effectiveShifts.some((s) => s.id === e.homeShiftId)) {
        (byId[e.homeShiftId] ??= []).push(e);
      } else {
        unassigned.push(e);
      }
    }
    const mode = seniorityMode.data ?? "rank_badge";
    const sortRoster = (list: Employee[]) =>
      list.slice().sort((a, b) => compareBySeniority(mode, a, b));
    const out: string[] = [];
    for (const s of shiftOrder) for (const e of sortRoster(byId[s.id] ?? [])) out.push(e.id);
    for (const e of sortRoster(unassigned)) out.push(e.id);
    return out;
  }, [employees.data, shifts.data, effectiveEmployees, effectiveShifts, seniorityMode.data]);

  function keyOf(employeeId: string, date: string) { return `${employeeId}|${date}`; }

  function cellsBetween(
    aEmp: string, aDate: string, bEmp: string, bDate: string,
  ): string[] {
    const empIdx = new Map(employeeOrder.map((id, i) => [id, i]));
    const dayIdx = new Map(days.map((d, i) => [ymd(d), i]));
    const i1 = empIdx.get(aEmp) ?? 0;
    const i2 = empIdx.get(bEmp) ?? 0;
    const j1 = dayIdx.get(aDate) ?? 0;
    const j2 = dayIdx.get(bDate) ?? 0;
    const lo = Math.min(i1, i2), hi = Math.max(i1, i2);
    const loD = Math.min(j1, j2), hiD = Math.max(j1, j2);
    const out: string[] = [];
    for (let i = lo; i <= hi; i++) {
      for (let j = loD; j <= hiD; j++) {
        out.push(keyOf(employeeOrder[i], ymd(days[j])));
      }
    }
    return out;
  }

  function selectCell(e: React.MouseEvent, employee: Employee, date: string) {
    if (!mayEditCells) return;
    const key = keyOf(employee.id, date);

    if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
      // Ctrl / Cmd click: toggle this cell in/out of the selection.
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      setSelAnchor({ employeeId: employee.id, date });
      return;
    }

    if (e.shiftKey && selAnchor) {
      // Shift click: select the rectangle between anchor and here.
      const range = cellsBetween(selAnchor.employeeId, selAnchor.date, employee.id, date);
      setSelection(new Set(range));
      return;
    }

    // Plain click: if this cell is already part of the current
    // selection, keep the selection intact — otherwise the first
    // click of an eventual double-click would collapse a multi-
    // selection to just this cell before the dblclick fires. If the
    // cell isn't in the selection, replace selection with just it.
    // Either way, set this cell as the anchor for Shift-click ranges.
    if (!selection.has(key)) {
      setSelection(new Set([key]));
    }
    setSelAnchor({ employeeId: employee.id, date });
  }

  function openPicker(e: React.MouseEvent, employee: Employee, date: string) {
    if (!mayEditCells) return;
    // Modifiers on dblclick aren't part of the selection workflow —
    // dblclick just means "open the options for this cell or the
    // active multi-selection". If the clicked cell somehow isn't in
    // the selection, open a single-cell picker on it.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const key = keyOf(employee.id, date);
    if (!selection.has(key)) {
      setSelection(new Set([key]));
      setSelAnchor({ employeeId: employee.id, date });
    }
    setPicker({ employee, date, anchor: rect });
  }

  // Open the inline editor over a cell — used by the page-level
  // type-to-edit shortcut. Looks up the cell's DOM rect by a
  // data-cell-key attribute (see the Section render) instead of
  // taking it from a React event, since the keystroke that
  // triggered this didn't originate from the cell itself.
  //
  // seed is what the editor starts with:
  //   * a printable character → overwrites the current value
  //     (Excel / Google Sheets behaviour)
  //   * "" (Enter / F2)       → preserves the visible chip so the
  //                              supervisor can correct it
  function beginInlineEdit(employee: Employee, date: string, seed: string) {
    if (!mayEditCells) return;
    const key = keyOf(employee.id, date);
    const cellEl = typeof document !== "undefined"
      ? document.querySelector<HTMLElement>(`[data-cell-key="${CSS.escape(key)}"]`)
      : null;
    if (!cellEl) return;
    const rect = cellEl.getBoundingClientRect();
    let resolvedSeed = seed;
    if (seed === "") {
      const existing = assignmentAt.get(key)
        ?? crossAssignmentAt.get(key)
        ?? null;
      resolvedSeed = (existing?.note ?? timeOffAt.get(key)?.code ?? "").trim();
    }
    setInlineEdit({ employee, date, anchor: rect, seed: resolvedSeed });
  }

  function applyCodeToSelection(
    code: string,
    opts: { force?: boolean } = {},
  ): { ok: true } | { ok: false; error: string } {
    // Validate + normalize before anything else. Rejects bare
    // numbers ("5"), ambiguous am/pm ("2p"), and typos that don't
    // parse as HH-HH; auto-pads single-digit hours so "9-14"
    // gets stored as "09-14". See normalizeCellCode() below the
    // component for the rule.
    const norm = normalizeCellCode(code);
    if (!norm.ok) {
      // Return the error to the caller so an open picker / inline
      // editor can display it in-form without closing. Callers
      // without a form context (keyboard delete) surface it via
      // setFlashError themselves.
      return { ok: false, error: norm.error };
    }
    const trimmed = norm.value;
    const empById = new Map((employees.data ?? []).map((e) => [e.id, e]));
    const firstShiftId = shifts.data?.[0]?.id ?? "";
    const targets = selection.size > 0
      ? Array.from(selection)
      : (picker ? [keyOf(picker.employee.id, picker.date)] : []);

    // Block off-codes (coverage < 1) on any cell whose officer is
    // already covering an OT somewhere else that same day. An
    // officer on overtime can't simultaneously be Off / Sick / At
    // Court / etc. — the supervisor has to remove the OT chip
    // first (on the covered shift's OT row), then mark them off.
    const newCov = trimmed
      ? shiftCodeByCode.get(trimmed)?.coverageValue
      : undefined;
    const isOffCode = trimmed !== "" && newCov !== undefined && newCov < 1;
    // Force mode skips the OT-elsewhere guard so an L2+ supervisor
    // can stamp a code onto a real-world situation the validation
    // doesn't understand. The forced write is marked in
    // overrideReason (see upsertMut below) so the cell renders in
    // bold red and "Revert override" can find it.
    if (isOffCode && !opts.force) {
      for (const key of targets) {
        const [empId, date] = key.split("|");
        const emp = empById.get(empId);
        if (!emp) continue;
        // Use the extended-range chip map — its keys already
        // reflect the calendar day the OT is ACTUALLY worked
        // (wrap-midnight aware). The prior version keyed on the
        // OT record's date field, which for wrap-midnight second-
        // half covers is one day BEHIND the day the officer is
        // physically on shift — so marking sick on the officer's
        // home day (before the OT day) got wrongly blocked. See
        // extendedRangeAt for the placement rules.
        const otRowsOnDate = extendedRangeAssignmentsAt.get(key) ?? [];
        const onOtElsewhere = otRowsOnDate.some((a) =>
          (a.note ?? "").trim().toUpperCase() === "OT"
        );
        if (onOtElsewhere) {
          return {
            ok: false,
            error:
              `${emp.lastName}, ${emp.firstName} is covering overtime on ${date}. `
              + `Remove the OT chip from that shift's coverage row before marking them ${trimmed}.`,
          };
        }
      }
    }

    for (const key of targets) {
      const [empId, date] = key.split("|");
      const emp = empById.get(empId);
      if (!emp) continue;
      const existing = assignmentAt.get(key) ?? null;
      // An "orphan" is an assignment row for this officer + date on
      // a shift OTHER than their current homeShiftId — usually
      // leftover from a previous shift bid (an XXX pattern row that
      // didn't get cleaned up when the officer moved shifts). The
      // cell renders the orphan note as a fallback, so clearing or
      // overwriting the cell needs to delete the orphan too —
      // otherwise the XXX appears to come back on the next render
      // because the underlying row is still in the database.
      const orphan = !existing ? crossAssignmentAt.get(key) ?? null : null;
      const shiftId = emp.homeShiftId ?? firstShiftId;
      if (!shiftId) continue;
      // NOTE: prior versions trimmed / deleted the underlying
      // time_off row when the supervisor cleared a schedule cell
      // that only had an approved time-off overlay. That was
      // destructive — a single-day request got wiped, and the
      // Completed list on the Time Off page lost the record. We
      // never touch time_off rows from the schedule now; a
      // supervisor who actually wants to cancel an approved
      // request does so from the Time Off page. See the picker
      // guard below that also hides the misleading "Remove" chip
      // button for time-off-only cells.
      // Clearing a cell whose only visible chip is a cross-shift OT
      // "extended range" (e.g. Colt Smith's B Set 8/2 showing 6-18
      // because he's covering A Set) has to reach across to that
      // OT row and delete it — otherwise the chip is derived from a
      // row on a different shift, so upsertMut's home-shift-only
      // delete can't touch it and the chip stubbornly persists.
      //
      // We look up by the CHIP's cell key, not (employeeId, date),
      // because a first-half wrap-midnight cover puts the chip on
      // date - 1 while the underlying OT row lives on date. The
      // extendedRangeAssignmentsAt map already keys off chip cell
      // → assignments so we drop whatever ones are backing the
      // visible chip, no matter where the row itself sits.
      if (!trimmed && !existing && !orphan) {
        const otRows = extendedRangeAssignmentsAt.get(key) ?? [];
        for (const a of otRows) deleteMut.mutate(a.id);
      }
      upsertMut.mutate({
        employeeId: emp.id, date, shiftId, code: trimmed,
        existing: existing ?? orphan,
        forced: !!opts.force,
      });

      // Coverage-suggestion detection: if the supervisor typed
      // hour-range hours (like "12-18") whose window extends past
      // the officer's home shift, offer to record the overflow as
      // OT on the appropriate adjacent shift. See coverageSuggestion
      // state definition above for the full flow.
      maybeSuggestOtForOverflow(emp, date, shiftId, trimmed);
    }
    return { ok: true };
  }

  // Runs after a successful hour-range cell save. If the typed hours
  // land outside the officer's home-shift window and the overflow
  // fits inside an adjacent rotation shift (prev or next in the same
  // department), stage a coverage-suggestion banner so the supervisor
  // can promote the overflow into a real OT record. Same-shift-only
  // notes and pure off-codes never trigger.
  function maybeSuggestOtForOverflow(
    emp: Employee, date: string, shiftId: string, note: string,
  ) {
    if (!note) return;
    const hrs = parseHourNote(note);
    if (!hrs) return;
    const home = (shifts.data ?? []).find((s) => s.id === shiftId);
    if (!home || home.inRotation === false) return;
    const homeStartH = hourOf(home.startTime);
    if (homeStartH === null) return;
    const homeDurationH = Math.max(1, Math.floor(home.durationMin / 60));
    const homeEndH = (homeStartH + homeDurationH) % 24;
    // Buckets the officer's typed range says they'll actually work.
    const typedBuckets = new Set(bucketsBetween(hrs[0], hrs[1]));
    const homeBuckets = new Set(bucketsBetween(homeStartH, homeEndH));
    // Overflow = typed hours that fall OUTSIDE the home window.
    const overflow = [...typedBuckets].filter((h) => !homeBuckets.has(h));
    if (overflow.length === 0) return;
    // Find an adjacent shift whose window contains all overflow buckets.
    const departmentShifts = (shifts.data ?? [])
      .filter((s) => s.departmentId === home.departmentId && s.inRotation !== false)
      .filter((s) => s.id !== home.id);
    let adj: Shift | null = null;
    let adjBuckets = new Set<number>();
    for (const s of departmentShifts) {
      const sStart = hourOf(s.startTime);
      if (sStart === null) continue;
      const sEnd = (sStart + Math.max(1, Math.floor(s.durationMin / 60))) % 24;
      const buckets = new Set(bucketsBetween(sStart, sEnd));
      if (overflow.every((h) => buckets.has(h))) {
        adj = s; adjBuckets = buckets; break;
      }
    }
    if (!adj || overflow.length === 0) return;
    // OT hours on the adjacent shift = the overflow buckets rendered
    // as HH:00 – HH:00. Take min/max in shift-index space (not modular
    // hour space) so wrap-midnight ranges format the right way round.
    const adjOrdered = bucketsBetween(hourOf(adj.startTime) ?? 0,
      ((hourOf(adj.startTime) ?? 0) + Math.max(1, Math.floor(adj.durationMin / 60))) % 24);
    const overflowInAdjOrder = adjOrdered.filter((h) => overflow.includes(h) && adjBuckets.has(h));
    if (overflowInAdjOrder.length === 0) return;
    const first = overflowInAdjOrder[0];
    const last = overflowInAdjOrder[overflowInAdjOrder.length - 1];
    const fmt = (h: number) => `${String(h).padStart(2, "0")}:00`;
    const otStart = fmt(first);
    const otEnd = fmt((last + 1) % 24);
    const label = `${emp.lastName}, ${emp.firstName}`.trim().replace(/^,\s*/, "") || emp.id.slice(0, 8);
    setCoverageSuggestion({
      employeeId: emp.id,
      employeeLabel: label,
      date, homeShift: home, adjacentShift: adj,
      otStart, otEnd, typedNote: note,
    });
  }

  // Called by the Yes button on the coverage-suggestion banner.
  // Creates a real OT record on the adjacent shift with the overflow
  // hours; auto-join logic in createAssignment will merge it with
  // any existing OT there so it renders as one paired chip.
  const suggestionAcceptMut = useMutation({
    mutationFn: async (args: {
      employeeId: string; shiftId: string; date: string;
      otStart: string; otEnd: string;
    }) => {
      await createAssignmentCompat({
        employeeId: args.employeeId,
        shiftId: args.shiftId,
        date: args.date,
        note: "OT",
        overrideReason: "Overtime — promoted from typed hours",
        coverageGroupId: crypto.randomUUID(),
        otStartTime: args.otStart,
        otEndTime: args.otEnd,
      });
    },
    onSuccess: () => { invalidate(); setCoverageSuggestion(null); },
    onError: (e: { message?: string } | Error) => {
      setFlashError(
        e && "message" in e && typeof e.message === "string" && e.message
          ? e.message
          : "Couldn't create the OT record.",
      );
    },
  });

  // Wrapper for callers without their own form (keyboard delete,
  // clipboard paste). Surfaces failures via the schedule-level
  // flash banner since no picker / inline editor is open to
  // catch the error inline.
  function applyCodeFallback(code: string) {
    const r = applyCodeToSelection(code);
    if (!r.ok) setFlashError(r.error);
  }

  // Dismiss the popover on outside click / Escape. Esc also clears selection.
  const popRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function away(ev: MouseEvent) {
      if (popRef.current && !popRef.current.contains(ev.target as Node)) {
        setPicker(null);
      }
    }
    if (picker) document.addEventListener("mousedown", away);
    function key(ev: KeyboardEvent) {
      // Ignore when the user is typing in an input or textarea.
      const t = ev.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (ev.key === "Escape") {
        setPicker(null);
        setSelection(new Set());
      } else if (ev.key === "Delete" || ev.key === "Backspace") {
        if (selection.size > 0) {
          ev.preventDefault();
          applyCodeFallback("");
        }
      } else if (
        // Type-to-edit: when a single cell is selected and the
        // supervisor starts typing, drop the inline editor over the
        // cell so they can enter a code without ever opening the
        // popover. Enter / F2 reuse the visible value as seed
        // (Excel's "edit in place"); a printable character
        // overwrites it (Excel's "type to replace"). Cmd/Ctrl keys
        // and the picker being open both short-circuit so copy /
        // paste and Enter-inside-popover keep working.
        !picker && !inlineEdit && selection.size === 1 && selAnchor
        && !ev.ctrlKey && !ev.metaKey && !ev.altKey
        && (ev.key === "Enter" || ev.key === "F2"
            || (ev.key.length === 1 && ev.key >= " "))
      ) {
        const emp = employeesById.get(selAnchor.employeeId);
        if (emp) {
          ev.preventDefault();
          const seed = ev.key === "Enter" || ev.key === "F2" ? "" : ev.key;
          beginInlineEdit(emp, selAnchor.date, seed);
        }
      } else if ((ev.ctrlKey || ev.metaKey) && ev.key === "c") {
        // Copy the anchor cell's code (if any) into the in-memory clipboard.
        if (selAnchor) {
          const a = assignmentAt.get(keyOf(selAnchor.employeeId, selAnchor.date));
          setClipboardCode((a?.note ?? "").trim().toUpperCase());
          ev.preventDefault();
        }
      } else if ((ev.ctrlKey || ev.metaKey) && ev.key === "v") {
        if (clipboardCode !== null && selection.size > 0) {
          ev.preventDefault();
          applyCodeFallback(clipboardCode);
        }
      }
    }
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [picker, inlineEdit, selection, selAnchor, clipboardCode,
      assignmentAt, crossAssignmentAt, timeOffAt,
      employees.data, employeesById, shifts.data]);

  // ---- Drag handlers ---------------------------------------------------
  const [dragHint, setDragHint] = useState<{ sectionId?: string; cellKey?: string } | null>(null);

  function dragEmployeeStart(e: DragEvent, emp: Employee) {
    e.dataTransfer.setData(DRAG_EMPLOYEE, emp.id);
    e.dataTransfer.effectAllowed = "move";
  }
  function dragAssignmentStart(e: DragEvent, a: Assignment) {
    e.dataTransfer.setData(
      DRAG_ASSIGNMENT,
      JSON.stringify({
        id: a.id, employeeId: a.employeeId, shiftId: a.shiftId, note: a.note,
        fromDate: a.date,
      }),
    );
    e.dataTransfer.effectAllowed = "move";
  }
  function dragOverSection(e: DragEvent, sectionId: string) {
    if (e.dataTransfer.types.includes(DRAG_EMPLOYEE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragHint({ sectionId });
    }
  }
  function dropOnSection(e: DragEvent, sectionId: string) {
    const empId = e.dataTransfer.getData(DRAG_EMPLOYEE);
    setDragHint(null);
    if (!empId || !sectionId) return;
    e.preventDefault();
    // Drag-and-drop onto a section header — same write the
    // section's "+ Add officer" button performs. Tac section
    // sets the tac home; regular / exempt sets the standard home.
    const sec = (shifts.data ?? []).find((s) => s.id === sectionId);
    const patch: Partial<Employee> = sec?.tacRole === "tac"
      ? { tacHomeShiftId: sectionId }
      : { homeShiftId: sectionId };
    moveOfficerMut.mutate({ id: empId, patch });
  }
  function dragOverCell(e: DragEvent, cellKey: string) {
    if (e.dataTransfer.types.includes(DRAG_ASSIGNMENT)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragHint({ cellKey });
    }
  }
  function dropOnCell(e: DragEvent, toDate: string) {
    const raw = e.dataTransfer.getData(DRAG_ASSIGNMENT);
    setDragHint(null);
    if (!raw) return;
    e.preventDefault();
    const payload = JSON.parse(raw) as {
      id: string; employeeId: string; shiftId: string; note: string | null;
      fromDate: string;
    };
    if (payload.fromDate === toDate) return;
    moveAssignmentMut.mutate({
      existingId: payload.id,
      employeeId: payload.employeeId,
      shiftId: payload.shiftId,
      note: payload.note,
      toDate,
    });
  }

  // ---- Grouping --------------------------------------------------------
  type Group = { shift: Shift | null; label: string; employees: Employee[] };
  const groups: Group[] = useMemo(() => {
    const active = effectiveEmployees.filter((e) => e.status !== "inactive");
    const shiftOrder = effectiveShifts.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const byId: Record<string, Employee[]> = {};
    const unassigned: Employee[] = [];
    // Officers appear under every shift they're scheduled to work
    // in the visible window — their home shift plus any temp
    // shift_assignments that overlap. So a rotating officer shows
    // up in both shift sections, with their cells filled only for
    // the days each shift is actually theirs (the cell renderer
    // handles that via shiftLookup).
    for (const e of active) {
      const placements = shiftLookup.shiftsFor(e.id);
      const fitted = [...placements].filter((id) => shiftById[id]);
      if (fitted.length === 0) {
        unassigned.push(e);
        continue;
      }
      for (const sid of fitted) {
        (byId[sid] ??= []).push(e);
      }
    }
    const mode = seniorityMode.data ?? "rank_badge";
    const sortRoster = (list: Employee[]) =>
      list.slice().sort((a, b) => compareBySeniority(mode, a, b));
    const out: Group[] = shiftOrder.map((s) => ({
      shift: s,
      label: s.name.toUpperCase(),
      employees: sortRoster(byId[s.id] ?? []),
    }));
    if (unassigned.length > 0) {
      out.push({ shift: null, label: "UNASSIGNED", employees: sortRoster(unassigned) });
    }
    return out;
  }, [effectiveEmployees, effectiveShifts, shiftById, shiftLookup, seniorityMode.data]);

  function navPayPeriod(deltaDays: number) {
    // Previous / Next / Today update this browser's view only; the
    // shared agency-wide anchor is only moved via the date picker
    // (Owner / L1 only), so a non-supervisor scrolling forward
    // doesn't jerk everyone else's schedule.
    setLocalAnchor(addDays(anchor, deltaDays));
  }

  if (needsRedirect && activeDept) {
    return <Navigate to={`/schedule/${activeDept.id}`} replace />;
  }

  // Mirrors the schedule grid's per-cell render priority (assignment
  // → time-off overlay → cross-shift orphan → IOD overlay → pattern
  // overlay), and accounts for the section gate so a section that
  // doesn't own the (officer, date) shows blank. Used both by the
  // PDF export and any future read-only renderers.
  function pdfCellText(emp: Employee, d: Date, g: ScheduleGroup): string {
    const date = ymd(d);
    const sectionShiftId = g.shift?.id ?? null;
    const effShiftId = shiftLookup.get(emp.id, date);
    const sectionOwnsCell =
      sectionShiftId === null || effShiftId === sectionShiftId;
    if (!sectionOwnsCell) return "";
    const cellKey = `${emp.id}|${date}`;
    const a = assignmentAt.get(cellKey);
    if (a) {
      const note = (a.note ?? "").trim();
      return note;
    }
    const ext = extendedRangeAt.get(cellKey);
    if (ext) return ext;
    const off = timeOffAt.get(cellKey);
    if (off) return off.code;
    const orphan = crossAssignmentAt.get(cellKey);
    if (orphan) return (orphan.note ?? "").trim();
    if (iodOverlayAt.has(cellKey)) return "IOD";
    if (patternOverlayAt.has(cellKey)) return "XXX";
    return "";
  }
  function pdfCellBg(emp: Employee, d: Date, g: ScheduleGroup): string | null {
    const text = pdfCellText(emp, d, g);
    if (!text) return null;
    const meta = shiftCodes.data?.find((c) => c.code.toUpperCase() === text.toUpperCase());
    if (!meta) return null;
    return meta.color ?? null;
  }

  function handleDownloadPdf() {
    const doc = generateSchedulePdf({
      agencyName: agency.data?.name ?? "Agency",
      departmentName: activeDept?.name ?? null,
      days,
      groups,
      cellText: pdfCellText,
      cellBg: pdfCellBg,
      coverageAt: (shift, d) => {
        const cov = coverageByShiftDate.get(`${shift.id}|${ymd(d)}`);
        return cov ?? null;
      },
      isShiftActiveOn: (shift, d) => isShiftActiveOn(agency.data, shift, ymd(d)),
      codes: shiftCodes.data ?? [],
      tacStart: agency.data?.tacModeActive
        ? (agency.data.tacModeStartDate ?? null)
        : null,
    });
    const range = `${ymd(days[0])}_to_${ymd(days[days.length - 1])}`;
    const deptSlug = (activeDept?.name ?? "Schedule").replace(/\s+/g, "-");
    doc.save(`Schedule_${deptSlug}_${range}.pdf`);
  }

  // Single source of truth for the schedule's table render. Called
  // once from the screen scroll container with the full 28-day
  // `days` array, and twice from the print-only block lower in the
  // JSX — first with days 0-13, then 14-27 — so each printed page
  // shows two readable weeks instead of the whole 4-week grid
  // crammed onto one landscape sheet.
  function renderScheduleTable(daySlice: Date[]) {
    return (
      <table
        className={
          // Beta: drop min-w-max so the 7 day columns + officer
          // column share the iPhone width exactly (no horizontal
          // scroll). Non-beta keeps the original behavior where
          // columns size to their max content and the table
          // overflows horizontally for scrolling.
          betaDesign
            ? "text-xs border-collapse w-full table-fixed schedule-table"
            : "text-xs border-collapse w-full min-w-max schedule-table"
        }
      >
        <thead>
          <tr className="text-slate-500">
            <th
              className={
                (betaDesign
                  // Beta: pin the header cell on mobile too (no md:
                  // gate) and narrow it to w-16 (64px) so the day
                  // columns get the most of the remaining ~330px
                  // budget on an iPhone.
                  ? "sticky left-0 top-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-1 py-2 text-left font-medium w-16"
                  : "md:sticky md:left-0 sticky top-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-3 py-2 text-left font-medium w-40 md:w-56")
              }
            >
              Employee
            </th>
            {daySlice.map((d, i) => (
              <th
                key={d.toISOString()}
                className={cellChrome(
                  d, today, i,
                  "sticky top-0 z-20 bg-slate-50 border-b border-slate-200 px-2 py-2 text-center font-medium" +
                  (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : ""),
                )}
                onMouseEnter={() => setHoveredCol(i)}
                style={i === hoveredCol ? HOVER_COL_STYLE : undefined}
              >
                <div className="uppercase tracking-wide text-[10px] text-slate-500">
                  {format(d, "EEE")}
                </div>
                <div className="text-slate-700">{format(d, "M/d")}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, gi) => (
            <Section
              key={g.shift?.id ?? "_unassigned"}
              group={g}
              days={daySlice}
              today={today}
              first={gi === 0}
              betaDesign={betaDesign}
              scenarioMode={scenarioMode}
              onEditShift={scenarioMode ? (id) => setEditingScenarioShift(id) : undefined}
              isDragHintedSection={dragHint?.sectionId === g.shift?.id}
              dragHintedCellKey={dragHint?.cellKey}
              onSectionDragOver={(e) => g.shift && dragOverSection(e, g.shift.id)}
              onSectionDrop={(e) => g.shift && dropOnSection(e, g.shift.id)}
              assignmentAt={assignmentAt}
              crossAssignmentAt={crossAssignmentAt}
              shiftById={shiftById}
              shiftCodes={shiftCodes.data ?? []}
              onOpenPicker={openPicker}
              onSelectCell={selectCell}
              selection={selection}
              hoveredCol={hoveredCol}
              onHoverCol={setHoveredCol}
              hoveredEmpId={hoveredEmpId}
              onHoverEmp={setHoveredEmpId}
              collapsed={collapsedShifts.has(g.shift?.id ?? "__unassigned__")}
              onToggleCollapsed={() => toggleShiftCollapsed(g.shift?.id ?? "__unassigned__")}
              mayEditRoster={mayEditRoster}
              meEmployeeId={me?.employeeId}
              onDragEmployee={dragEmployeeStart}
              onDragAssignmentStart={dragAssignmentStart}
              onCellDragOver={dragOverCell}
              onCellDrop={dropOnCell}
              onAddEmployeeToShift={(s, employeeId) => {
                if (scenarioMode && scenario) {
                  // Scenario: write the override + unexclude.
                  const nextOverrides = {
                    ...(scenario.employeeShiftOverrides ?? {}),
                    [employeeId]: s.id,
                  };
                  Promise.all([
                    api.updateScenario(scenario.id, { employeeShiftOverrides: nextOverrides }),
                    scenarioExcluded.has(employeeId)
                      ? api.includeScenarioEmployee(scenario.id, employeeId)
                      : Promise.resolve(),
                  ])
                    .then(() => {
                      qc.invalidateQueries({ queryKey: ["scenario", scenarioParam] });
                      qc.invalidateQueries({ queryKey: ["scenarioExcluded", scenarioParam] });
                    })
                    .catch((e: { message?: string }) => setFlashError(e?.message ?? "Add failed."));
                  return;
                }
                const patch: Partial<Employee> = s.tacRole === "tac"
                  ? { tacHomeShiftId: s.id }
                  : { homeShiftId: s.id };
                moveOfficerMut.mutate({ id: employeeId, patch });
              }}
              onRemoveFromShift={(emp) => {
                const sId = g.shift?.id;
                if (!sId) return;
                if (scenarioMode && scenario) {
                  // Scenario: remove by marking the employee as
                  // "unassigned" within the scenario (shift override
                  // null → shiftLookup groups them under the empty
                  // section instead of this shift's).
                  const nextOverrides = {
                    ...(scenario.employeeShiftOverrides ?? {}),
                    [emp.id]: null,
                  };
                  api.updateScenario(scenario.id, { employeeShiftOverrides: nextOverrides })
                    .then(() => qc.invalidateQueries({ queryKey: ["scenario", scenarioParam] }))
                    .catch((e: { message?: string }) => setFlashError(e?.message ?? "Remove failed."));
                  return;
                }
                const patch: Partial<Employee> = {};
                if (emp.homeShiftId === sId) patch.homeShiftId = null;
                if (emp.tacHomeShiftId === sId) patch.tacHomeShiftId = null;
                moveOfficerMut.mutate({ id: emp.id, patch });
              }}
              addableEmployees={(employees.data ?? []).filter(
                (e) => e.status !== "inactive" && e.homeShiftId !== g.shift?.id,
              )}
              coverageByShiftDate={coverageByShiftDate}
              otByShiftDate={otByShiftDate}
              extendedRangeAt={extendedRangeAt}
              timeOffAt={timeOffAt}
              cellNotesAt={cellNotesAt}
              iodOverlayAt={iodOverlayAt}
              patternOverlayAt={patternOverlayAt}
              employeesById={employeesById}
              onFixCoverage={mayEditRoster
                ? (shiftId, date) => setFixCoverage({ shiftId, date })
                : undefined}
              onEditCoverage={mayEditRoster
                ? (rows, shiftId, date) => setEditingCoverage({ rows, shiftId, date })
                : undefined}
              shiftLookup={shiftLookup}
              isShiftActiveOnDate={(date) =>
                !g.shift || isShiftActiveOn(agency.data, g.shift, date)}
            />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Scenario mode: red "Mock schedule" banner across the top
        * carrying Publish / Extend / Back actions. Stays visible
        * while editing so there's never any doubt the admin is
        * working against a draft and not the live schedule. The
        * normal page header below still renders (dept name + nav),
        * so the editor keeps the full Schedule layout familiarity. */}
      {scenarioMode && scenario && (
        <div className="shrink-0 border-b border-red-400/60 bg-red-600 text-white print:hidden">
          <div className="px-3 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="hidden sm:inline uppercase text-[10px] md:text-xs bg-white/15 border border-white/20 rounded px-1.5 py-0.5 font-semibold shrink-0">
                Mock schedule
              </span>
              <div className="min-w-0">
                <div className="text-sm md:text-base font-semibold truncate">
                  {scenario.name}
                  <span className="ml-2 font-normal opacity-80 text-xs md:text-sm">
                    · {scenario.startDate} → {scenario.endDate}
                    {scenarioReadonly ? " · PUBLISHED" : ""}
                  </span>
                </div>
                <div className="text-[11px] opacity-80 hidden md:block">
                  Edits here don't touch the live schedule, dashboard, coverage, or notifications. Publish to replace the live schedule for this window.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/mock-schedules"
                className="text-xs font-medium underline-offset-2 hover:underline"
              >
                ← All scenarios
              </Link>
              {!scenarioReadonly && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(
                        `Publish "${scenario.name}" and replace the live schedule for this window?\n\nThis cannot be undone — the scenario will be archived after publish.`,
                      )) return;
                      api.publishScenario(scenario.id)
                        .then(() => {
                          qc.invalidateQueries({ queryKey: ["assignments"] });
                          qc.invalidateQueries({ queryKey: ["coverage"] });
                          qc.invalidateQueries({ queryKey: ["timeoff"] });
                          nav("/mock-schedules");
                        })
                        .catch((e: { message?: string }) => setFlashError(e?.message ?? "Publish failed."));
                    }}
                    className="rounded bg-white text-red-700 hover:bg-red-50 text-xs font-semibold px-3 py-1 shadow-sm"
                  >
                    Publish →
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {flashError && (
        <div className="mx-6 mt-6 mb-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 shrink-0">
          <span className="flex-1">{flashError}</span>
          <button
            type="button"
            onClick={() => setFlashError(null)}
            className="text-red-400 hover:text-red-700 font-semibold leading-none"
            title="Dismiss"
          >×</button>
        </div>
      )}
      {coverageSuggestion && (
        <div className="mx-6 mt-6 mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 shrink-0 dark:bg-indigo-500/25 dark:border-indigo-700 dark:text-indigo-100">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="font-semibold whitespace-nowrap">Looks like OT:</span>
            <span className="flex-1 min-w-[240px]">
              You entered <span className="font-mono">{coverageSuggestion.typedNote}</span>{" "}
              for {coverageSuggestion.employeeLabel} on{" "}
              {coverageSuggestion.date}, but {coverageSuggestion.homeShift.name.trim()}{" "}
              ends before that. Record{" "}
              <span className="font-mono">{coverageSuggestion.otStart}–{coverageSuggestion.otEnd}</span>{" "}
              as OT on {coverageSuggestion.adjacentShift.name.trim()} so it
              shows up in coverage?
            </span>
            <div className="flex gap-2 shrink-0 ml-auto">
              <button
                type="button"
                onClick={() => {
                  const s = coverageSuggestion;
                  suggestionAcceptMut.mutate({
                    employeeId: s.employeeId,
                    shiftId: s.adjacentShift.id,
                    date: s.date,
                    otStart: s.otStart,
                    otEnd: s.otEnd,
                  });
                }}
                disabled={suggestionAcceptMut.isPending}
                className="rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold px-3 py-1 text-xs disabled:opacity-50"
              >
                {suggestionAcceptMut.isPending ? "Recording…" : "Yes, record it"}
              </button>
              <button
                type="button"
                onClick={() => setCoverageSuggestion(null)}
                className="rounded border border-amber-400 text-amber-900 hover:bg-amber-100 px-3 py-1 text-xs font-medium dark:border-indigo-700 dark:text-indigo-100 dark:hover:bg-indigo-500/40"
              >
                No, keep as-is
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mobile: cancel <main>'s pt-12 (which reserves space for
        * the fixed hamburger) with -mt-12 so the controls row
        * shares the vertical band with the hamburger. flex-col-
        * reverse puts the controls row first and the title row
        * below. Desktop: original two-column header, no offset
        * — the hamburger isn't visible on md+ anyway.
        *
        * Beta drops the -mt-12 so the title row sits below the
        * hamburger / bell instead of overlapping them. The beta
        * header is also the only nav now (no Previous/Today/Next
        * buttons to share the band with). */}
      <header
        className={
          "flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-1 md:gap-0 mb-1 md:mb-4 px-4 md:px-6 pt-3 md:pt-6 shrink-0 md:mt-0 "
          + (betaDesign ? "" : "-mt-12")
          + (scenarioMode ? " hidden" : "")
        }
      >
        <div className="min-w-0">
          {/* Mobile: one tiny line, all info inline. */}
          <div className="md:hidden flex items-baseline gap-1.5 truncate text-xs leading-tight">
            <span className="font-semibold text-slate-800">
              {activeDept?.name ?? "Schedule"}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500 truncate">
              {format(days[0], "MMM d")} – {format(days[days.length - 1], "MMM d, yyyy")}
            </span>
            {selection.size > 1 && (
              <span className="ml-1 inline-flex items-center text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-0 print:hidden">
                {selection.size}
              </span>
            )}
          </div>
          {/* Desktop: original h1 + subtitle layout. */}
          <h1 className="hidden md:block text-xl font-semibold text-slate-900 truncate">
            {activeDept?.name ?? "Schedule"}
          </h1>
          <p className="hidden md:block text-sm text-slate-500">
            {format(days[0], "MMM d")} – {format(days[days.length - 1], "MMM d, yyyy")}
            {!betaDesign && " (4 weeks)"}
            <span className="ml-3 text-slate-400 print:hidden">
              · click to pick a code · Shift-click range · Ctrl-click toggle
              · Ctrl+C / Ctrl+V / Del · Esc clears
            </span>
            {selection.size > 1 && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 print:hidden">
                {selection.size} selected
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap md:flex-nowrap pl-12 pr-12 md:pl-0 md:pr-0 print:hidden">
          {/* Beta hides the anchor/Previous/Today/Next chrome — the
            * window is pinned to "around today" so there's nothing
            * to navigate. */}
          {!betaDesign && (
            <>
              <label className="text-xs text-slate-500 flex items-center gap-1.5">
                <span className="hidden md:inline">Start</span>
                <input
                  type="date"
                  value={ymd(days[0])}
                  disabled={!maySetAnchor || setAnchorMut.isPending}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const [y, m, dd] = v.split("-").map(Number);
                    const next = new Date(y, (m ?? 1) - 1, dd ?? 1);
                    next.setHours(0, 0, 0, 0);
                    setSharedAnchor(next);
                  }}
                  className="text-xs px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                  title={maySetAnchor
                    ? "Change the shared pay period anchor for every user"
                    : "Only an Owner or Level 1 account can change the shared pay period anchor."}
                />
              </label>
              <Button
                variant="secondary" size="sm"
                onClick={() => navPayPeriod(-scheduleDays)}
                title={`Show the previous ${scheduleWeeks} weeks (local to this browser)`}
              >
                <span className="md:hidden">‹</span>
                <span className="hidden md:inline">← Previous</span>
              </Button>
              <Button
                variant="secondary" size="sm"
                title={`Jump to the ${scheduleWeeks}-week page containing today`}
                onClick={() => {
                  const t = new Date(); t.setHours(0, 0, 0, 0);
                  const msPerDay = 86_400_000;
                  const diff = Math.floor((t.getTime() - anchor.getTime()) / msPerDay);
                  const pages = Math.floor(diff / scheduleDays);
                  if (pages !== 0) setLocalAnchor(addDays(anchor, pages * scheduleDays));
                  else setLocalAnchor(anchor);
                }}
              >
                Today
              </Button>
              <Button
                variant="secondary" size="sm"
                onClick={() => navPayPeriod(scheduleDays)}
                title={`Show the next ${scheduleWeeks} weeks (local to this browser)`}
              >
                <span className="md:hidden">›</span>
                <span className="hidden md:inline">Next →</span>
              </Button>
              {/* Per-device 2-week / 4-week toggle. 4 is the pay-
                * period default; 2 halves the visible window so
                * the grid fits on laptop screens without a
                * horizontal scroll. Toggles the whole page in
                * place — Previous / Next / Today all page by the
                * new width, and the deep-link snap follows too. */}
              <Button
                variant="secondary" size="sm"
                onClick={() => setScheduleWeeks((w) => (w === 4 ? 2 : 4))}
                title={
                  scheduleWeeks === 4
                    ? "Switch to a 2-week window (better on smaller screens)"
                    : "Switch back to the full 4-week pay-period window"
                }
              >
                {scheduleWeeks === 4 ? "4wk" : "2wk"}
              </Button>
            </>
          )}
          <Button
            variant="secondary" size="sm"
            onClick={() => window.print()}
            title={`Open the browser print dialog — ${scheduleWeeks} weeks on one landscape page.`}
            className="hidden md:inline-flex"
          >
            <span className="md:hidden">⎙</span>
            <span className="hidden md:inline">Print</span>
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={handleDownloadPdf}
            title="Download a typeset PDF — same two-week split per page, deterministic layout."
            className="hidden md:inline-flex"
          >
            <span className="md:hidden">PDF</span>
            <span className="hidden md:inline">Download PDF</span>
          </Button>
        </div>
      </header>

      {/* On-call rotations — today's primary/secondary across every
        * roster, one compact line. Lives outside the schedule's
        * scroll container; the full 4-week roster grid lives on
        * the dedicated On-Call page. Renders nothing when the
        * "oncall" flag is off. Scenario mode hides it entirely —
        * mock schedules don't need the live on-call snapshot. */}
      {!scenarioMode && <OnCallTodayStrip />}

      {(employees.data ?? []).length === 0 && (
        <div className="mb-4 mx-6 p-4 bg-sky-50 border border-sky-200 rounded-md text-sm text-sky-900 shrink-0">
          No employees yet. Add them on <strong>Employees</strong>, or load
          25 placeholders from <strong>Settings → Sample data</strong>.
        </div>
      )}

      <div
        // The page is a flex column pinned to main's height; this
        // wrapper takes all remaining vertical room (flex-1 +
        // min-h-0) and owns both overflow axes. That makes it the
        // nearest scrolling ancestor for the sticky thead's
        // `top-0` so the date header actually sticks, and there's
        // no leftover dead space at the bottom of the viewport
        // for a stray scrollbar to park in.
        //
        // Mobile gets `snap-y snap-proximity` so vertical scrolling
        // through a long schedule eases onto each shift header
        // instead of free-wheeling past them. Proximity (not
        // mandatory) so the snap only kicks in when the user is
        // already near a header — fast flicks still glide
        // naturally. Desktop opts out (`md:snap-none`) because
        // mouse-wheel users find any snap annoying.
        //
        // For print we DON'T split the table — the browser print
        // path renders all four weeks on one landscape page
        // (zoom 0.58 in styles.css) the way it used to. The
        // typeset PDF download (Download PDF button) handles the
        // legibility case by splitting into two pages at typeset
        // resolution instead of relying on the browser's print
        // engine.
        className="bg-white border-t border-slate-200 overflow-auto flex-1 min-h-0 snap-y snap-proximity md:snap-none"
        onMouseLeave={() => { setHoveredCol(null); setHoveredEmpId(null); }}
        // Beta swipe-to-paginate. Records the initial X on
        // touchstart and on touchend compares the delta — if it's
        // beyond a threshold AND mostly horizontal, advances /
        // reverses the week offset. The 50px / horizontal-dominance
        // checks avoid hijacking vertical scrolls and tiny taps.
        onTouchStart={(e) => {
          if (!betaDesign) return;
          const t = e.touches[0];
          (e.currentTarget as unknown as { __swipe?: { x: number; y: number } })
            .__swipe = { x: t.clientX, y: t.clientY };
        }}
        onTouchEnd={(e) => {
          if (!betaDesign) return;
          const start = (e.currentTarget as unknown as { __swipe?: { x: number; y: number } }).__swipe;
          if (!start) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - start.x;
          const dy = t.clientY - start.y;
          (e.currentTarget as unknown as { __swipe?: unknown }).__swipe = undefined;
          if (Math.abs(dx) < 50) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.5) return; // vertical-dominant, ignore
          setBetaWeekOffset((o) => o + (dx < 0 ? 1 : -1));
        }}
      >
        {renderScheduleTable(days)}
      </div>

      {picker && (() => {
        // The popover treats either the home-shift assignment, a
        // visible orphan (cross-shift leftover row), or the cross-
        // shift OT row driving an extended-range chip as the
        // "existing" chip — that way "Remove code" works on the row
        // the supervisor actually sees, even when that row lives on
        // a shift the officer is no longer rostered to (orphan) or a
        // neighboring shift they're covering (OT).
        //
        // For the OT case we consult extendedRangeAssignmentsAt,
        // keyed on the CHIP's cell — the underlying OT row can be
        // on a different date (first-half wrap covers) so a same-
        // (employee, date) scan would miss it and hide "Remove code".
        const pkey = `${picker.employee.id}|${picker.date}`;
        const pOtRow = (!assignmentAt.get(pkey) && !crossAssignmentAt.get(pkey))
          ? extendedRangeAssignmentsAt.get(pkey)?.[0] ?? null
          : null;
        const pExisting = assignmentAt.get(pkey)
          ?? crossAssignmentAt.get(pkey)
          ?? pOtRow
          ?? null;
        return (
          <CodePickerPopover
            ref={popRef}
            anchor={picker.anchor}
            employee={picker.employee}
            date={picker.date}
            codes={shiftCodes.data ?? []}
            existing={pExisting}
            timeOffCode={pExisting ? null : timeOffAt.get(pkey)?.code ?? null}
            partialTimeOff={(() => {
              const t = timeOffAt.get(pkey);
              if (t && t.startTime && t.endTime) return t;
              return null;
            })()}
            cellNotes={cellNotesAt.get(pkey) ?? []}
            currentUserId={me?.id ?? null}
            canDeleteAnyNote={mayEditRoster}
            onAddNote={(text) => {
              addCellNoteMut.mutate({
                employeeId: picker.employee.id,
                date: picker.date,
                note: text,
              });
            }}
            onDeleteNote={(id) => deleteCellNoteMut.mutate(id)}
            selectionSize={selection.size}
            onPick={applyCodeToSelection}
            onClear={() => applyCodeToSelection("")}
            onForce={mayForceOverride
              ? (code) => applyCodeToSelection(code, { force: true })
              : undefined}
            onClose={() => setPicker(null)}
            onViewHistory={mayViewAudit ? () => {
              // Route to the Audit page pre-filtered to this cell.
              // Uses the officer's current effective home shift when
              // no assignment exists in the cell — that's the shift
              // the cell is displaying under, so it's the right
              // audit scope for "what happened here".
              const empHome = picker.employee.homeShiftId ?? "";
              const targetShift = pExisting?.shiftId ?? empHome;
              const params = new URLSearchParams({
                employee: picker.employee.id,
                date: picker.date,
                shift: targetShift,
              });
              setPicker(null);
              nav(`/audit?${params.toString()}`);
            } : undefined}
          />
        );
      })()}

      {inlineEdit && (
        <InlineCellEditor
          anchor={inlineEdit.anchor}
          codes={shiftCodes.data ?? []}
          seed={inlineEdit.seed}
          onCommit={applyCodeToSelection}
          onClear={() => applyCodeToSelection("")}
          onClose={() => setInlineEdit(null)}
        />
      )}

      {fixCoverage && (() => {
        const sh = (shifts.data ?? []).find((s) => s.id === fixCoverage.shiftId);
        if (!sh) return null;
        return (
          <FixCoverageModal
            shift={sh}
            date={fixCoverage.date}
            onClose={() => setFixCoverage(null)}
          />
        );
      })()}

      {editingCoverage && (() => {
        const sh = (shifts.data ?? []).find((s) => s.id === editingCoverage.shiftId);
        if (!sh) return null;
        return (
          <FixCoverageModal
            shift={sh}
            date={editingCoverage.date}
            replacingRows={editingCoverage.rows}
            onClose={() => setEditingCoverage(null)}
          />
        );
      })()}

      {editingScenarioShift && scenario && (() => {
        const sh = (shifts.data ?? []).find((s) => s.id === editingScenarioShift);
        if (!sh) return null;
        return (
          <ScenarioShiftEditModal
            shift={sh}
            onClose={() => setEditingScenarioShift(null)}
            onSave={(patch) => {
              const nextOverrides = {
                ...(scenario.shiftOverrides ?? {}),
                [sh.id]: {
                  ...(scenario.shiftOverrides?.[sh.id] ?? {}),
                  ...patch,
                },
              };
              api.updateScenario(scenario.id, { shiftOverrides: nextOverrides })
                .then(() => {
                  qc.invalidateQueries({ queryKey: ["scenario", scenarioParam] });
                  setEditingScenarioShift(null);
                })
                .catch((e: { message?: string }) => setFlashError(e?.message ?? "Save failed."));
            }}
          />
        );
      })()}
    </div>
  );
}

function ScenarioShiftEditModal({
  shift, onClose, onSave,
}: {
  shift: Shift;
  onClose: () => void;
  onSave: (patch: { name: string; startTime: string; durationMin: number }) => void;
}) {
  const [name, setName] = useState(shift.name);
  const [startTime, setStartTime] = useState(shift.startTime);
  const [endTime, setEndTime] = useState(
    shiftEndTime(shift.startTime, shift.durationMin),
  );
  function parseMin(s: string): number | null {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  }
  function deriveDurationMin(): number | null {
    const s = parseMin(startTime);
    const e = parseMin(endTime);
    if (s === null || e === null) return null;
    const diff = e - s;
    if (diff === 0) return 24 * 60;
    return diff > 0 ? diff : diff + 24 * 60;
  }
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center pt-16 print:hidden"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-[min(28rem,calc(100vw-2rem))] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-2xl">
        <header className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
            Edit shift (scenario)
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
          >
            ×
          </button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-slate-500">
            Changes stay inside this scenario. The live shift is only
            affected when the scenario is published.
          </p>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
            Shift name
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
              Start time
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
              />
            </label>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
              End time
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
              />
            </label>
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              const dur = deriveDurationMin();
              if (!name.trim() || dur === null) return;
              onSave({ name: name.trim(), startTime, durationMin: dur });
            }}
            disabled={!name.trim() || deriveDurationMin() === null}
            className="px-3 py-1.5 text-sm rounded bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50"
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---- Section subtree ----------------------------------------------------

interface SectionProps {
  group: { shift: Shift | null; label: string; employees: Employee[] };
  days: Date[];
  today: Date;
  first: boolean;
  isDragHintedSection: boolean;
  dragHintedCellKey: string | undefined;
  onSectionDragOver: (e: DragEvent<HTMLElement>) => void;
  onSectionDrop: (e: DragEvent<HTMLElement>) => void;
  assignmentAt: Map<string, Assignment>;
  // Cross-shift assignments (OT or orphan off-codes) keyed by
  // employee|date. Used by the home cell render to show the
  // extended-hours chip or fall back to an orphan code when the
  // employee has no home-shift assignment for that day.
  crossAssignmentAt: Map<string, Assignment>;
  shiftById: Record<string, Shift>;
  shiftCodes: ShiftCode[];
  onOpenPicker: (e: React.MouseEvent, emp: Employee, date: string) => void;
  onSelectCell: (e: React.MouseEvent, emp: Employee, date: string) => void;
  selection: Set<string>;
  mayEditRoster: boolean;
  meEmployeeId: string | null | undefined;
  onDragEmployee: (e: DragEvent<HTMLDivElement>, emp: Employee) => void;
  onDragAssignmentStart: (e: DragEvent<HTMLDivElement>, a: Assignment) => void;
  onCellDragOver: (e: DragEvent<HTMLTableCellElement>, cellKey: string) => void;
  onCellDrop: (e: DragEvent<HTMLTableCellElement>, toDate: string) => void;
  onAddEmployeeToShift: (shift: Shift, employeeId: string) => void;
  onRemoveFromShift: (employee: Employee) => void;
  addableEmployees: Employee[];
  coverageByShiftDate: Map<string, {
    assigned: number; minStaffing: number;
    oicAssigned: number; oicRequired: number;
    overridden: boolean;
  }>;
  // Cross-shift OT assignments attached to this Section's shift,
  // indexed by `${shiftId}|${date}`. Each entry becomes an OT row.
  otByShiftDate: Map<string, Assignment[]>;
  // For each (employee, date) where the employee is doing OT on a
  // neighboring shift, the extended worked-hours label to display
  // in their home cell ("21-11" etc).
  extendedRangeAt: Map<string, string>;
  // (employeeId|date) → approved time-off overlay info. `code` is
  // the letter to render (V/PL/…); startTime + endTime are set only
  // for partial-day rows and drive the amber wash + tooltip on
  // cells whose assignment happens to overlap the partial window.
  timeOffAt: Map<string, {
    code: string;
    typeLabel: string;
    startTime: string | null;
    endTime: string | null;
    note: string | null;
  }>;
  // (employeeId|date) → attached free-form supervisor notes. Cells
  // with any notes get the same amber wash a partial-time-off cell
  // gets so a supervisor can spot at a glance that there's context
  // beyond the visible chip.
  cellNotesAt: Map<string, CellNote[]>;
  // (employeeId|date) → true when an IOD period covers this cell.
  // Used as a last-resort overlay for unassigned officers whose
  // row has no shift to write IOD assignment rows against.
  iodOverlayAt: Map<string, true>;
  // (employeeId|date) → true when the unassigned officer's
  // days-off pattern says they're off this weekday. Materialiser
  // can't write XXX rows without a shift, so the grid falls back
  // to this overlay.
  patternOverlayAt: Map<string, true>;
  employeesById: Map<string, Employee>;
  // When provided, red coverage cells become clickable and open the
  // fix-coverage modal. Undefined for non-supervisors.
  onFixCoverage?: (shiftId: string, date: string) => void;
  // Undefined for non-supervisors; without it the coverage chip
  // isn't clickable. Called on double-click of a coverage chip —
  // the modal lets the supervisor swap each officer or wipe the
  // whole chip, with notifications fanning out either way.
  onEditCoverage?: (rows: Assignment[], shiftId: string, date: string) => void;
  // When true, hide every row under the shift header (date row,
  // officer rows, coverage row, OIC row, OT rows). Click the
  // header to toggle. Undefined → header not clickable (unassigned
  // rosters don't collapse).
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  // Shared hover highlight — column index under the cursor across
  // the whole schedule. Cells at this index paint a faint blue so
  // the full column reads as one unit while the user scans a row.
  hoveredCol: number | null;
  onHoverCol: (i: number | null) => void;
  // Companion to hoveredCol: the employee id for the row under
  // the cursor. Officer rows tint the same faint blue across all
  // their day cells so the row + column form a crosshair.
  hoveredEmpId: string | null;
  onHoverEmp: (id: string | null) => void;
  // Per-(officer,date) effective shift lookup. Used to grey out
  // cells when the officer is on a different shift that day
  // (rotating shift assignment in effect). Cells where the
  // officer's effective shift matches this section's shift render
  // normally; cells where it doesn't render faded + non-
  // interactive so it's clear they're "borrowed" from another
  // shift's view of the same officer.
  shiftLookup: { get(employeeId: string, date: string): string | null };
  // Per-date "is this section's shift in rotation today" gate.
  // Returns false on tac-transition pages for the regular shifts
  // post-transition and the tac shifts pre-transition. Those days
  // render as N/A rather than 0/3 short on the coverage row + OIC
  // row, and the officer cells dim out.
  isShiftActiveOnDate: (date: string) => boolean;
  // iOS beta-design mode: condense officer-row labels to initials.
  // Owner-only and iOS-only — see useBetaDesign in lib/platform.ts.
  betaDesign: boolean;
  // Scenario mode flag — flattens every shift bar to red so it's
  // obvious the admin is editing a draft, not the live grid.
  scenarioMode?: boolean;
  // Click handler for the "Edit shift" button that only appears
  // in scenario mode — opens the shift-override modal.
  onEditShift?: (shiftId: string) => void;
}

function Section(p: SectionProps) {
  const { group, days, first } = p;
  // Scenario mode flattens every shift bar to the same muted slate
  // grey — makes "this is a mock" obvious without the red being
  // visually loud; the admin can still tell shifts apart from the
  // name text alone.
  const sectionColor = p.scenarioMode
    ? "#64748b"
    : (group.shift?.color ?? "#64748b");
  // The bar uses the shift's full neon hue as the background and
  // picks a white-or-black text color via luminance so bright
  // yellow / lime keep black text and the deeper hues flip to
  // white. A soft inner divider colour stays muted (slate-500
  // on yellow, white/70 on blue) so the "06:00–18:00 · 8
  // officers" metadata reads as secondary text.
  const headerText = readableTextOn(sectionColor);
  const metaText = headerText === "#ffffff"
    ? "rgba(255,255,255,0.78)"
    : "rgba(15,23,42,0.65)";
  const rowCount = group.employees.length;

  return (
    <>
      {!first && (
        <tr>
          <td colSpan={days.length + 1} className="h-4 bg-slate-100 border-0" />
        </tr>
      )}
      <tr
        onDragOver={p.onSectionDragOver}
        onDrop={p.onSectionDrop}
        // snap-start + scroll-margin-top so vertical scrolling on
        // mobile eases each shift header into view just below the
        // sticky date row, instead of letting it slip past. The
        // sticky date row is ~48px tall (two-line "EEE / M/d" plus
        // py-2), so scroll-mt-14 (56px) gives the shift bar a few
        // pixels of clearance below the date strip — without the
        // extra cushion the bar's top edge ends up hidden behind
        // the date strip and only the text inside the bar shows.
        // Desktop ignores snap (set via `md:snap-none` on the
        // scroll container) so mouse-wheel users keep their free
        // scroll.
        className={
          "text-[11px] uppercase tracking-wider font-semibold snap-start scroll-mt-14 "
          + (p.isDragHintedSection ? "outline outline-2 outline-sky-400 outline-offset-[-2px]" : "")
        }
      >
        <th
          colSpan={days.length + 1}
          className={
            "themed-fill text-left px-3 py-1 border-y border-slate-200 "
            + (p.onToggleCollapsed ? "cursor-pointer select-none" : "")
          }
          style={{
            color: headerText,
            backgroundColor: sectionColor,
            ["--themed-color" as string]: sectionColor,
          } as CSSProperties}
          onClick={p.onToggleCollapsed}
          title={p.onToggleCollapsed
            ? (p.collapsed ? "Click to expand this shift" : "Click to collapse this shift")
            : undefined}
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-block w-3 text-center transition-transform"
              style={{ transform: p.collapsed ? "rotate(-90deg)" : undefined }}
            >▾</span>
            <span>●</span>
            <span>{group.label}</span>
            {group.shift && (
              <span className="font-normal normal-case opacity-80 dark:opacity-70" style={{ color: metaText }}>
                · {group.shift.startTime}–{shiftEndTime(group.shift.startTime, group.shift.durationMin)} · {rowCount} officer{rowCount === 1 ? "" : "s"}
                {p.collapsed && <span className="ml-2 italic" style={{ color: metaText, opacity: 0.8 }}>collapsed</span>}
              </span>
            )}
            {p.scenarioMode && group.shift && p.onEditShift && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); p.onEditShift!(group.shift!.id); }}
                className="ml-auto text-xs font-normal normal-case opacity-80 hover:opacity-100 underline-offset-2 hover:underline"
                style={{ color: metaText }}
                title="Rename this shift / change its times (scenario only)"
              >
                Edit shift
              </button>
            )}
          </div>
        </th>
      </tr>
      {p.collapsed ? null : <SectionBody p={p} />}
    </>
  );
}

// The body of a shift section — date row, per-officer rows, add
// officer button, Coverage row, OIC row, OT rows. Split out so the
// Section header can short-circuit rendering when collapsed.
function SectionBody({ p }: { p: SectionProps }) {
  const { group, days, today, assignmentAt, shiftCodes } = p;
  const codeMap = useMemo(
    () => new Map(shiftCodes.map((c) => [c.code.toUpperCase(), c])),
    [shiftCodes],
  );
  return (
    <>
      {group.employees.map((emp) => {
        const isMe = !!p.meEmployeeId && emp.id === p.meEmployeeId;
        const isHoveredRow = p.hoveredEmpId === emp.id;
        return (
        <tr
          key={emp.id}
          className={
            "border-b border-slate-100" +
            (isMe ? " bg-sky-50/60 ring-1 ring-sky-300 ring-inset" : "")
          }
          onMouseEnter={() => p.onHoverEmp(emp.id)}
          title={isMe ? "This is you" : undefined}>
          <td
            className={
              (p.betaDesign
                // Beta: always-sticky 64px column so the full week
                // fits an iPhone with no horizontal scroll. Tight
                // padding + overflow-hidden truncate gracefully
                // when initials + a long radio number would
                // otherwise wrap.
                ? "sticky left-0 z-10 border-r border-slate-200 px-1 py-0.5 whitespace-nowrap w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 border-r border-slate-200 px-3 py-0.5 whitespace-nowrap ") +
              (isMe ? "bg-sky-50" : "bg-white")
            }
            style={isHoveredRow
              ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
              : undefined}
          >
            <div className="flex items-center gap-2">
              {/* × remove button — supervisor-only on shifts that
                * accept a roster. Hidden in beta because the column
                * is too narrow for affordances and the action lives
                * elsewhere in the iOS reskin (TBD — for now you can
                * still drag to reassign). */}
              {!p.betaDesign && group.shift && p.mayEditRoster && (
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    p.onRemoveFromShift(emp);
                  }}
                  title={`Remove ${emp.lastName} from this shift`}
                  className="shrink-0 text-slate-400 hover:text-white hover:bg-red-500 rounded w-4 h-4 flex items-center justify-center text-xs leading-none transition border border-slate-300 hover:border-red-500 print:hidden"
                >
                  ×
                </button>
              )}
              <div
                draggable
                onDragStart={(e) => p.onDragEmployee(e, emp)}
                className="cursor-grab active:cursor-grabbing select-none flex-1 min-w-0"
                title="Drag to another shift to reassign home shift"
              >
              <div className="flex items-center gap-1.5">
                <span
                  className={
                    // Beta: OIC officers stand out as bold royal-blue
                    // (no separate OIC badge — the color IS the badge).
                    // Non-OIC stay in the default name treatment.
                    p.betaDesign && emp.isOic
                      ? "font-bold text-blue-700"
                      : "font-medium text-slate-900"
                  }
                >
                  {p.betaDesign
                    ? `${emp.firstName?.[0] ?? ""}.${emp.lastName?.[0] ?? ""}.`
                    : <>{emp.rank ? `${emp.rank}. ` : ""}{emp.lastName}, {emp.firstName}</>}
                </span>
                {/* OIC badge — hidden in beta since the name color
                  * conveys the same information without taking a
                  * second visual slot. */}
                {!p.betaDesign && emp.isOic && (
                  <span className="inline-block px-1 py-0 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200 dark:bg-indigo-500/30 dark:text-indigo-100 dark:border-indigo-700">
                    OIC
                  </span>
                )}
                {/* FTO badge — trainee riding along with a training
                  * officer. Shown in both beta and non-beta so
                  * supervisors always see who isn't a countable seat. */}
                {emp.isFto && (
                  <span
                    className="inline-block px-1 py-0 text-[9px] font-semibold rounded bg-sky-100 text-sky-800 border border-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-700"
                    title="In training — not counted toward coverage"
                  >
                    FTO
                  </span>
                )}
                {(emp.radioNumber ?? emp.badgeNumber) && (
                  <span className="text-[10px] text-slate-400 font-mono">
                    {/* Prefer radio call sign over badge — supervisors
                      * address officers by radio number on the air, so it
                      * reads faster on the schedule than the badge.
                      * Falls back to badge when no radio is on file. */}
                    #{emp.radioNumber ?? emp.badgeNumber}
                  </span>
                )}
              </div>
              </div>
            </div>
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cellKey = `${emp.id}|${date}`;
            const a = assignmentAt.get(cellKey);
            const homeNote = a?.note?.trim().toUpperCase() ?? "";
            const homeCode = homeNote ? codeMap.get(homeNote) : undefined;
            const isDropHinted = p.dragHintedCellKey === cellKey;
            const isSelected = p.selection.has(cellKey);
            // When the officer is rendered under multiple shift
            // sections (a temporary shift_assignments row in
            // effect), grey out the days they're NOT on this
            // section's shift so the supervisor can see at a
            // glance which section "owns" them that day. The
            // unassigned section has shift = null and shows all
            // their cells (they're not under any shift to begin
            // with).
            const effShiftId = p.shiftLookup.get(emp.id, date);
            const sectionOwnsCell = p.group.shift === null
              || effShiftId === p.group.shift.id;
            // Rendering priority for the home cell:
            //  1. Cross-shift overtime on this date — show the
            //     extended-hours chip (e.g. "22-11"). Takes priority
            //     over a home-shift off-code because the officer
            //     *just picked up OT*; hiding it behind an XXX /
            //     vacation chip would make the OT assignment
            //     invisible. Removing the OT restores the off-code.
            //  2. A home-shift off-code (XXX, S, V, …) — show it.
            //  3. A home-shift custom/working note that isn't an
            //     off-code — show it as free-text.
            //  4. Fallback orphan (cross-shift off-code left behind
            //     after a home-shift change) — show its note so it
            //     doesn't silently disappear.
            //  5. Empty.
            const isOffCode = !!homeCode && homeCode.coverageValue < 1;
            const extRange = p.extendedRangeAt.get(cellKey);
            // Approved time-off overlay. Only kicks in when the
            // cell is otherwise blank (no assignment) — a manual
            // edit on that day takes precedence so the supervisor
            // can stamp a more specific code without re-processing
            // the time-off request.
            const timeOffEntry = p.timeOffAt.get(cellKey);
            // Time-off overlay only replaces an empty cell (no
            // assignment) when it's a WHOLE-day request. Partial-
            // hours requests never take over — they paint an amber
            // background + a tooltip on top of the working cell,
            // handled below via partialTimeOff.
            const wholeDayTimeOff = timeOffEntry
              && !timeOffEntry.startTime
              && !timeOffEntry.endTime;
            const partialTimeOff = timeOffEntry
              && timeOffEntry.startTime
              && timeOffEntry.endTime
              ? timeOffEntry : null;
            const timeOffCode = !a && wholeDayTimeOff ? timeOffEntry.code : undefined;
            const timeOffMeta = timeOffCode ? codeMap.get(timeOffCode) : undefined;
            const orphan = !a && !extRange && !timeOffCode
              ? p.crossAssignmentAt.get(cellKey)
              : undefined;
            const orphanNote = orphan?.note?.trim().toUpperCase() ?? "";
            const orphanCode = orphanNote ? codeMap.get(orphanNote) : undefined;
            // Unassigned-officer IOD overlay. Only kicks in when
            // nothing else fills the cell — the actual IOD
            // assignment rows for shift-assigned officers are
            // picked up via `a` above.
            const iodOverlay = !a && !extRange && !timeOffCode && !orphan
              && p.iodOverlayAt.has(cellKey);
            const iodOverlayMeta = iodOverlay ? codeMap.get("IOD") : undefined;
            // Days-off pattern overlay (XXX) for unassigned
            // officers. Lower priority than the IOD overlay above
            // so an IOD range visually trumps a regular off-day.
            const patternOverlay = !a && !extRange && !timeOffCode && !orphan && !iodOverlay
              && p.patternOverlayAt.has(cellKey);
            const patternOverlayMeta = patternOverlay ? codeMap.get("XXX") : undefined;
            const partialTitle = partialTimeOff
              ? `${partialTimeOff.typeLabel} ${partialTimeOff.startTime}–${partialTimeOff.endTime}`
                + (partialTimeOff.note ? `\n${partialTimeOff.note}` : "")
              : undefined;
            return (
              <td
                key={date}
                data-cell-key={cellKey}
                onClick={sectionOwnsCell ? (e) => p.onSelectCell(e, emp, date) : undefined}
                onDoubleClick={sectionOwnsCell ? (e) => p.onOpenPicker(e, emp, date) : undefined}
                onDragOver={sectionOwnsCell ? (e) => p.onCellDragOver(e, cellKey) : undefined}
                onDrop={sectionOwnsCell ? (e) => p.onCellDrop(e, date) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "p-0 h-6 min-w-[52px] text-center align-middle border border-slate-200 relative" +
                  (sectionOwnsCell ? " cursor-pointer" : " cursor-default bg-slate-100/70") +
                  (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : "") +
                  // Attention wash for cells carrying a partial
                  // time-off OR one or more supervisor notes. Light
                  // mode: a soft amber tint. Dark mode: teal — a
                  // cool hue that stays distinct from the violet
                  // pay-weekend bookend (so a cell that happens to
                  // be BOTH a pay-weekend edge AND has notes still
                  // reads two signals cleanly).
                  ((partialTimeOff || (p.cellNotesAt.get(cellKey)?.length ?? 0) > 0)
                    ? " bg-amber-100/70 dark:bg-teal-500/25" : "") +
                  (isDropHinted ? " outline outline-2 outline-sky-400" : "") +
                  (isSelected ? " outline outline-2 outline-sky-500 outline-offset-[-2px] z-10" : ""),
                )}
                style={i === p.hoveredCol || isHoveredRow
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!sectionOwnsCell
                  ? "On a different shift this day"
                  : (extRange
                    ? `Working overtime — ${extRange}`
                    : partialTitle
                      ?? ((p.cellNotesAt.get(cellKey)?.length ?? 0) > 0
                        ? "Cell has notes — double-click to view"
                        : undefined))}
              >
                {!sectionOwnsCell ? null : extRange ? (
                  <div className="w-full h-full flex items-center justify-center bg-amber-100 text-amber-800 text-[11px] font-semibold leading-none select-none dark:bg-indigo-500/30 dark:text-indigo-100">
                    {extRange}
                  </div>
                ) : isOffCode ? (
                  <CellChip
                    note={homeNote}
                    codeMeta={homeCode}
                    draggable={!!a}
                    onDragStart={a ? (e) => p.onDragAssignmentStart(e, a) : undefined}
                    forced={isForcedOverride(a)}
                  />
                ) : homeNote ? (
                  <CellChip
                    note={homeNote}
                    codeMeta={homeCode}
                    draggable={!!a}
                    onDragStart={a ? (e) => p.onDragAssignmentStart(e, a) : undefined}
                    forced={isForcedOverride(a)}
                  />
                ) : timeOffCode ? (
                  <CellChip
                    note={timeOffCode}
                    codeMeta={timeOffMeta}
                    draggable={false}
                  />
                ) : partialTimeOff ? (
                  // Partial-day time-off overlay: cell keeps reading
                  // as a working cell (the shift is still valid),
                  // shows the officer's WORKED range plus the type
                  // code chip, matching the "08-13 V" convention a
                  // supervisor would type by hand. Full detail
                  // (including the requester's note) is one double-
                  // click away in the code-picker's amber banner.
                  <div className="w-full h-full flex items-center justify-center gap-1 text-[10px] font-semibold leading-none select-none text-amber-900 dark:text-indigo-100">
                    {p.group.shift && (() => {
                      const label = partialWorkedLabel(
                        p.group.shift,
                        partialTimeOff.startTime!,
                        partialTimeOff.endTime!,
                      );
                      return label ? (
                        <span className="tabular-nums">{label}</span>
                      ) : null;
                    })()}
                    <span className="px-1 rounded bg-amber-200/80 dark:bg-indigo-500/50">
                      {partialTimeOff.code}
                    </span>
                  </div>
                ) : orphan ? (
                  <CellChip
                    note={orphanNote}
                    codeMeta={orphanCode}
                    draggable={false}
                    forced={isForcedOverride(orphan)}
                  />
                ) : iodOverlay ? (
                  <CellChip
                    note="IOD"
                    codeMeta={iodOverlayMeta}
                    draggable={false}
                  />
                ) : patternOverlay ? (
                  <CellChip
                    note="XXX"
                    codeMeta={patternOverlayMeta}
                    draggable={false}
                  />
                ) : (
                  <CellChip note="" codeMeta={undefined} draggable={false} />
                )}
              </td>
            );
          })}
        </tr>
        );
      })}
      {/* + Add officer row — hidden in beta since the iOS reskin
        * doesn't have a home for the inline picker yet (TBD; the
        * picker dropdown also assumes a desktop-sized popover). */}
      {group.shift && p.mayEditRoster && !p.betaDesign && (
        <tr className="border-b border-slate-100 print:hidden">
          <td colSpan={days.length + 1} className="md:sticky md:left-0 z-10 bg-white px-3 py-0.5">
            <AddOfficerButton
              shift={group.shift}
              addable={p.addableEmployees}
              onPick={(emp) => p.onAddEmployeeToShift(group.shift!, emp.id)}
            />
          </td>
        </tr>
      )}
      {group.shift && (() => {
        // One OT row per "group slot" so every coverage event gets
        // its own cell. Row count = max number of coverage groups on
        // any single day across the visible range. On days with
        // fewer groups than the row count the extra rows render an
        // empty cell. Rendered above Coverage / OIC so the
        // coverage-check rows sit at the section's bottom where the
        // supervisor's eye naturally lands.
        const shiftId = group.shift.id;
        const groupsByDay = days.map((d) =>
          groupOTByCoverage(p.otByShiftDate.get(`${shiftId}|${ymd(d)}`) ?? []),
        );
        const rowCount = groupsByDay.reduce((m, g) => Math.max(m, g.length), 0);
        if (rowCount === 0) return null;

        return (
          <>
            {Array.from({ length: rowCount }, (_, rowIdx) => (
              <tr key={`ot-${rowIdx}`} className="border-b border-slate-100 bg-amber-50/60 dark:bg-indigo-500/15">
                <td
                  className={
                    (p.betaDesign
                      ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                      : "md:sticky md:left-0 z-10 px-3 ") +
                    "bg-amber-50/60 border-r border-slate-200 py-0.5 whitespace-nowrap dark:bg-indigo-500/15"
                  }
                >
                  {rowIdx === 0 && (
                    <>
                      <span className="text-[10px] font-semibold text-amber-700 mr-1.5">OT</span>
                      <span className="text-slate-500 text-[11px]">Coverage</span>
                    </>
                  )}
                </td>
                {days.map((d, i) => {
                  const date = ymd(d);
                  const g = groupsByDay[i][rowIdx];
                  // OT chips don't render on dates the shift isn't
                  // in rotation — same gate the Coverage / OIC rows
                  // already use. Prevents an old OT entry on a
                  // tac-transition straddling page from leaking
                  // into the post-transition columns where the
                  // shift is N/A.
                  const shiftActive = p.isShiftActiveOnDate(date);
                  // "Excess" = the shift is currently over its
                  // minimum staffing on this date. When true, every
                  // OT chip on this (shift, date) renders in bright
                  // green as a "review — one of these can probably
                  // come out" signal. Mirror of how the Coverage
                  // cell paints red when short. Deliberately does
                  // NOT skip on `overridden` — the user's directive:
                  // overrides are temporary workarounds and the
                  // excess flag should still fire so nothing gets
                  // silently forgotten under an override.
                  const cov = p.coverageByShiftDate.get(`${shiftId}|${date}`);
                  const excess = shiftActive
                    && !!cov
                    && cov.assigned > cov.minStaffing;
                  return (
                    <td
                      key={date}
                      onMouseEnter={() => p.onHoverCol(i)}
                      style={i === p.hoveredCol
                        ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                        : undefined}
                      className={cellChrome(
                        d, today, i,
                        "p-0 h-6 text-center align-middle border border-slate-200 relative" +
                        (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : "") +
                        (shiftActive ? "" : " bg-slate-100"),
                      )}
                    >
                      {shiftActive && g && (
                        <OTGroupChip
                          rows={g}
                          employeesById={p.employeesById}
                          onEdit={p.onEditCoverage
                            ? (rows) => p.onEditCoverage!(rows, shiftId, date)
                            : undefined}
                          excess={excess}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </>
        );
      })()}
      {group.shift && !p.scenarioMode && (
        <tr className="border-t border-slate-200">
          <td
            className={
              (p.betaDesign
                ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 px-3 ") +
              "bg-slate-50 border-r border-slate-200 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500"
            }
          >
            Coverage
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cov = p.coverageByShiftDate.get(`${group.shift!.id}|${date}`);
            const assigned = cov?.assigned ?? 0;
            const required = cov?.minStaffing ?? group.shift!.minStaffing;
            const oicAssigned = cov?.oicAssigned ?? 0;
            const oicRequired = cov?.oicRequired ?? 1;
            // Shift not in rotation on this date (transition page,
            // regular shift on a tac date, or vice versa). Render
            // N/A so the cell doesn't blare red 0/3 short for a
            // shift that isn't actually scheduled to run that day.
            const shiftActive = p.isShiftActiveOnDate(date);
            // Supervisor override flips "short" off regardless of
            // the underlying bucket math. The number still renders
            // honestly so the supervisor sees the raw count.
            const overridden = cov?.overridden ?? false;
            const totalShort = shiftActive && !overridden && assigned < required;
            const oicShort = shiftActive && !overridden && oicAssigned < oicRequired;
            const short = totalShort || oicShort;
            const reasons: string[] = [];
            if (totalShort) reasons.push(`${fmtNum(assigned)} of ${required} working`);
            if (oicShort) reasons.push(`${fmtNum(oicAssigned)} of ${oicRequired} OIC`);
            // No inRotation gate — the Fix Coverage modal's Cross-
            // department tab can pull from anyone in the agency, so
            // specialty shifts (Dispatch, ACO, K-9) can be covered
            // too. The modal itself handles "no eligible candidates"
            // gracefully on a per-tab basis when there's truly
            // nothing to pick from.
            // Coverage cells are always clickable for supervisors —
            // short cells open the modal to fill the gap, fully-
            // covered cells open the same modal so the supervisor
            // can still add an officer (a planned extra, a coverage
            // pre-fill for an upcoming time-off request, etc.). The
            // hover treatment differentiates: red → red hover (fix),
            // grey → slate hover (add extra).
            const canEdit = !!p.onFixCoverage && shiftActive;
            return (
              <td
                key={d.toISOString()}
                onClick={canEdit ? () => p.onFixCoverage!(group.shift!.id, ymd(d)) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "px-1 py-1 text-center text-[10px] font-mono " +
                  (i > 0 && d.getDay() === 0 ? "border-l-2 border-l-slate-300 " : "") +
                  (!shiftActive
                    ? "bg-slate-100 text-slate-300"
                    : short
                      ? "bg-red-100 text-red-800 font-bold"
                      : "text-slate-500") +
                  (canEdit
                    ? " cursor-pointer " + (short ? "hover:bg-red-200" : "hover:bg-slate-100")
                    : ""),
                )}
                style={i === p.hoveredCol
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!shiftActive
                  ? `${group.shift!.name} isn't scheduled on this date`
                  : overridden
                  ? `Marked covered (override) — raw count ${fmtNum(assigned)} of ${required}`
                  : short
                  ? (canEdit
                    ? `Click to fix — ${reasons.join("; ")}`
                    : `Not covered — ${reasons.join("; ")}`)
                  : (canEdit
                    ? `Covered ${fmtNum(assigned)} of ${required} — click to add an officer`
                    : `Covered: ${fmtNum(assigned)} of ${required}`)}
              >
                {!shiftActive ? "—" : (<>{fmtNum(assigned)}/{required}</>)}
                {shiftActive && overridden && (
                  <span
                    className="ml-1 text-[8px] font-semibold align-middle text-amber-600"
                    title="Coverage override"
                  >
                    ✓
                  </span>
                )}
              </td>
            );
          })}
        </tr>
      )}
      {group.shift && group.shift.requiresOic !== false && !p.scenarioMode && (
        <tr className="border-b border-slate-200">
          <td
            className={
              (p.betaDesign
                ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 px-3 ") +
              "bg-slate-50 border-r border-slate-200 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500"
            }
          >
            OIC
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cov = p.coverageByShiftDate.get(`${group.shift!.id}|${date}`);
            const oicAssigned = cov?.oicAssigned ?? 0;
            const oicRequired = cov?.oicRequired ?? 1;
            const shiftActive = p.isShiftActiveOnDate(date);
            const overridden = cov?.overridden ?? false;
            const short = shiftActive && !overridden && oicAssigned < oicRequired;
            // No inRotation gate — the Fix Coverage modal's Cross-
            // department tab can pull from anyone in the agency, so
            // specialty shifts (Dispatch, ACO, K-9) can be covered
            // too. The modal itself handles "no eligible candidates"
            // gracefully on a per-tab basis when there's truly
            // nothing to pick from.
            // OIC cell is clickable whether short or covered — the
            // modal handles "I just want to add another supervisor"
            // and "fill the missing OIC" the same way.
            const canEdit = !!p.onFixCoverage && shiftActive;
            return (
              <td
                key={d.toISOString()}
                onClick={canEdit ? () => p.onFixCoverage!(group.shift!.id, ymd(d)) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "px-1 py-1 text-center text-[10px] font-mono " +
                  (i > 0 && d.getDay() === 0 ? "border-l-2 border-l-slate-300 " : "") +
                  (!shiftActive
                    ? "bg-slate-100 text-slate-300"
                    : short
                      ? "bg-red-100 text-red-800 font-bold"
                      : "text-slate-500") +
                  (canEdit
                    ? " cursor-pointer " + (short ? "hover:bg-red-200" : "hover:bg-slate-100")
                    : ""),
                )}
                style={i === p.hoveredCol
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!shiftActive
                  ? `${group.shift!.name} isn't scheduled on this date`
                  : short
                  ? (canEdit
                    ? `Click to fix — ${fmtNum(oicAssigned)} of ${oicRequired} OIC`
                    : `No OIC: ${fmtNum(oicAssigned)} of ${oicRequired} required`)
                  : (canEdit
                    ? `OIC covered ${fmtNum(oicAssigned)} of ${oicRequired} — click to add an OIC`
                    : `OIC covered: ${fmtNum(oicAssigned)} of ${oicRequired}`)}
              >
                {!shiftActive ? "—" : (<>{fmtNum(oicAssigned)}/{oicRequired}</>)}
              </td>
            );
          })}
        </tr>
      )}
    </>
  );
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Cell-input normalization + validation. Runs on every commit
// (custom text, inline-editor typed value, bulk paste). The rule
// is intentionally strict on hour ranges so garbage like "5" or
// ambiguous 12-hour clock entries ("9-5", "2p") can't slip in:
//
//   * Empty              → clear (unchanged)
//   * Letter-first       → shift code, uppercased, accepted as-is.
//                          (SA, XXX, V, TR, OT½, S1, custom codes.)
//   * Digit-first        → MUST parse as HH-HH in 24-hour format.
//                          Auto-pads single digits ("9-14" → "09-14")
//                          and accepts wrap-midnight ranges ("22-06").
//                          Rejects a bare number, an am/pm suffix, or
//                          anything without a proper HH-HH shape.
export function normalizeCellCode(
  input: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const s = input.trim();
  if (s === "") return { ok: true, value: "" };
  if (/^[A-Za-z]/.test(s)) return { ok: true, value: s.toUpperCase() };
  const cleaned = s.toUpperCase()
    .replace(/\s*HRS?$/, "")
    .replace(/\s+TO\s+/, "-")
    .replace(/\s+/g, "")
    .replace(/[–—]/g, "-");
  const m = cleaned.match(/^(\d{1,2})-(\d{1,2})$/);
  if (!m) {
    return {
      ok: false,
      error: `"${s}" isn't a valid entry. For custom hours use 24-hour format like 06-14 or 22-06 (dash between two hours). For codes, start with a letter.`,
    };
  }
  const sh = Number(m[1]);
  const eh = Number(m[2]);
  if (sh < 0 || sh > 23 || eh < 0 || eh > 23) {
    return {
      ok: false,
      error: `Hours must be 00-23 (24-hour format). Got "${s}".`,
    };
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return { ok: true, value: `${pad(sh)}-${pad(eh)}` };
}

// Shift a YYYY-MM-DD string by ±N calendar days without dragging in
// date-fns just for this. Used by the extended-range chip placement
// to hop the chip to the covering officer's home-cell date when the
// gap or their home shift wraps midnight.
function shiftYmd(dateStr: string, delta: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Group a day's cross-shift OT assignments into coverage events.
// Rows sharing the same coverage_group_id form one group (a split OT
// pair or a solo Full OT). Legacy rows without a group id each
// become their own group so they still render individually.
function groupOTByCoverage(rows: Assignment[]): Assignment[][] {
  const groups: Assignment[][] = [];
  const byGroup = new Map<string, Assignment[]>();
  for (const a of rows) {
    if (!a.coverageGroupId) {
      groups.push([a]);
      continue;
    }
    const bucket = byGroup.get(a.coverageGroupId);
    if (bucket) bucket.push(a);
    else {
      const fresh: Assignment[] = [a];
      byGroup.set(a.coverageGroupId, fresh);
      groups.push(fresh);
    }
  }
  return groups;
}

function initialsOf(e: Employee | undefined): string {
  if (!e) return "??";
  const f = (e.firstName ?? "").trim()[0] ?? "";
  const l = (e.lastName ?? "").trim()[0] ?? "";
  return (f + l).toUpperCase() || "??";
}

// A single "JJ-CS" / "JB" chip on the consolidated OT row. Pairs
// show first-half officer then second-half. We sort OT½s by
// override_reason (so "first half" comes before "second half" per
// what the Fix Coverage modal writes); if both rows happen to be
// OT½s with no distinguishing reason, fall back to createdAt order.
//
// `excess` flips the chip from the neutral amber to a bright
// emerald when the shift is currently over its minimum staffing on
// this date — mirror of how the Coverage row paints red when
// short. Signal is "one of these OT chips can probably come out";
// the supervisor decides which.
function OTGroupChip({
  rows, employeesById, onEdit, excess,
}: {
  rows: Assignment[];
  employeesById: Map<string, Employee>;
  // When provided, double-clicking the chip opens the coverage
  // edit modal (swap officer or remove entirely). Replaces the
  // older always-visible × button.
  onEdit?: (rows: Assignment[]) => void;
  excess?: boolean;
}) {
  const ordered = [...rows].sort((a, b) => {
    const ar = (a.overrideReason ?? "").toLowerCase();
    const br = (b.overrideReason ?? "").toLowerCase();
    const aFirst = ar.includes("first half") ? 0 : ar.includes("second half") ? 2 : 1;
    const bFirst = br.includes("first half") ? 0 : br.includes("second half") ? 2 : 1;
    if (aFirst !== bFirst) return aFirst - bFirst;
    return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
  });
  const isPair = ordered.length > 1;
  const label = ordered.map((a) => initialsOf(employeesById.get(a.employeeId))).join("-");
  const names = ordered
    .map((a) => {
      const e = employeesById.get(a.employeeId);
      return e ? `${e.firstName} ${e.lastName}` : "Unknown";
    })
    .join(isPair ? " + " : "");
  const codeLabel = isPair ? "OT split" : "OT full";
  const chipCls = excess
    ? "bg-emerald-200 text-emerald-900 font-bold dark:bg-emerald-900/50 dark:text-emerald-100"
    : "bg-amber-100 text-amber-800 dark:bg-indigo-500/30 dark:text-indigo-100";
  return (
    <div
      className={
        "w-full h-full flex items-center justify-center text-[11px] font-semibold leading-none select-none "
        + chipCls
        + (onEdit ? " cursor-pointer" : "")
      }
      onDoubleClick={onEdit
        ? (e) => { e.stopPropagation(); onEdit(ordered); }
        : undefined}
      title={
        (excess
          ? "Excess coverage — shift is over minimum staffing on this date. "
          : "")
        + `${codeLabel} · ${names}`
        + (onEdit ? " · Double-click to edit or remove." : "")
      }
    >
      {label}
    </div>
  );
}

// One cell's visual chip. Fills the cell completely — the background
// colour of any "off" / "partial" code paints the entire square so
// shortages read at a glance.
function CellChip({
  note, codeMeta, draggable, onDragStart, forced,
}: {
  note: string;
  codeMeta: ShiftCode | undefined;
  draggable: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  // True when the assignment behind this chip was force-saved by
  // an L2+ supervisor bypassing a validation error. Rendered in
  // bold red so it's obvious the normal guard was overridden.
  forced?: boolean;
}) {
  const base =
    "w-full h-full flex items-center justify-center text-[11px] font-semibold leading-none select-none"
    + (forced ? " text-red-700 font-black" : "");
  if (!note) {
    return <div className={base + " text-transparent"}>·</div>;
  }
  if (codeMeta) {
    // OT / OT½ get the same soft amber treatment as the
    // consolidated OT row chip ("JA", "BS-CS", …) so a cell-level
    // OT and the row-level OT visually belong to the same family.
    // The raw shift_codes.color (#f59e0b — strong orange) was
    // landing as a solid block that read louder than the OT row.
    const isOtCode =
      codeMeta.code === "OT" || codeMeta.code === "OT½"
      || codeMeta.code === "OT/2";
    if (isOtCode) {
      return (
        <div
          className={base + " bg-amber-100 text-amber-800 dark:bg-indigo-500/30 dark:text-indigo-100"}
          title={forced ? "Forced override — " + (codeMeta.label || codeMeta.code) : (codeMeta.label || codeMeta.code)}
          draggable={draggable}
          onDragStart={onDragStart}
        >
          {codeMeta.code}
        </div>
      );
    }
    // Status codes render transparent so the row reads quietly —
    // only the generated overtime chips (handled elsewhere, not
    // through CellChip) paint a background. An explicit per-code
    // colour configured in Settings still wins, for agencies that
    // want to flag specific codes. Forced overrides paint the note
    // text bold red (see base above) but keep the code's background
    // so the underlying meaning is still visible.
    const bg = codeMeta.color ?? "transparent";
    return (
      <div
        className={base + (forced ? "" : " text-slate-700")}
        style={{ backgroundColor: bg }}
        title={forced ? "Forced override — " + (codeMeta.label || codeMeta.code) : (codeMeta.label || codeMeta.code)}
        draggable={draggable}
        onDragStart={onDragStart}
      >
        {codeMeta.code}
      </div>
    );
  }
  // Free-text custom code (e.g. "9-4", "124"). Transparent too.
  return (
    <div
      className={base + (forced ? "" : " text-slate-800")}
      title={forced ? "Forced override — " + note : note}
      draggable={draggable}
      onDragStart={onDragStart}
    >
      {note}
    </div>
  );
}

// ---- Empty slot row ----------------------------------------------------

// ---- Code-picker popover -----------------------------------------------

const CodePickerPopover = forwardRef<HTMLDivElement, {
  anchor: DOMRect;
  employee: Employee;
  date: string;
  codes: ShiftCode[];
  existing: Assignment | null;
  // Non-null when the cell is currently showing an approved
  // time-off overlay (no assignment). Enables the "Remove" button
  // and the "Approved time off" status label so the supervisor can
  // edit the chip the same way as a regular assignment chip.
  timeOffCode: string | null;
  // Non-null when the cell has an approved PARTIAL time-off overlay
  // (whole-day rows come through `timeOffCode` instead). Renders as
  // an info banner at the top of the popover so a supervisor
  // (mobile especially, no hover state) can see the type + hours +
  // note without leaving the cell.
  partialTimeOff: {
    code: string;
    typeLabel: string;
    startTime: string | null;
    endTime: string | null;
    note: string | null;
  } | null;
  // Free-form supervisor notes attached to this cell. Rendered as
  // chips at the top of the picker with a small input under them
  // for adding new ones. Not shown in bulk-edit mode (they'd apply
  // to a single cell only anyway).
  cellNotes: CellNote[];
  currentUserId: string | null;
  canDeleteAnyNote: boolean;
  onAddNote: (text: string) => void;
  onDeleteNote: (id: string) => void;
  selectionSize: number;
  // Return-a-result variants so the picker can surface validation
  // failures IN-FORM (red banner at the top of the popover) and
  // stay open, instead of the schedule-level flash banner that
  // slammed the popover shut on the first typo.
  onPick: (code: string) => { ok: true } | { ok: false; error: string };
  onClear: () => { ok: true } | { ok: false; error: string };
  onClose: () => void;
  // Only set when the current user has viewAudit permission. When
  // present, the popover shows a "View change history" link at the
  // bottom that jumps to the Audit page pre-filtered to this cell.
  onViewHistory?: () => void;
  // Only set when the current user has forceCellOverride permission
  // (L2+). Called from the in-form error banner's "Force save
  // anyway" button — bypasses the OT-elsewhere guard on the parent
  // side and stamps the resulting row with the force-override tag
  // so the cell renders bold red. If forcing itself fails (e.g. an
  // approved-leave block returned a different error) the second
  // error surfaces the same way and the popover stays open.
  onForce?: (code: string) => { ok: true } | { ok: false; error: string };
}>(function CodePickerPopover(
  { anchor, employee, date, codes, existing, timeOffCode, partialTimeOff,
    cellNotes, currentUserId, canDeleteAnyNote,
    onAddNote, onDeleteNote,
    selectionSize, onPick, onClear, onClose, onViewHistory, onForce }, ref,
) {
  const [custom, setCustom] = useState("");
  // Track what the LAST attempt was so "Force save anyway" knows
  // what code to re-submit with the force flag.
  const [pickerError, setPickerError] = useState<
    { message: string; lastCode: string } | null
  >(null);
  function submitPick(code: string) {
    const r = onPick(code);
    if (!r.ok) { setPickerError({ message: r.error, lastCode: code }); return; }
    setPickerError(null);
    onClose();
  }
  function submitForce() {
    if (!pickerError || !onForce) return;
    const r = onForce(pickerError.lastCode);
    if (!r.ok) { setPickerError({ message: r.error, lastCode: pickerError.lastCode }); return; }
    setPickerError(null);
    onClose();
  }
  function submitClear() {
    const r = onClear();
    if (!r.ok) { setPickerError({ message: r.error, lastCode: "" }); return; }
    setPickerError(null);
    onClose();
  }

  const [noteDraft, setNoteDraft] = useState("");
  function submitNote() {
    const t = noteDraft.trim();
    if (!t) return;
    onAddNote(t);
    setNoteDraft("");
  }

  // Every open of the popover starts scrolled to the top so the
  // Officer + Date header + Remove-code / time-off banner are
  // visible from the get-go. Without this the browser preserves
  // the last scroll position across mounts (in some layouts), and
  // an opened picker could land mid-code-list instead of at the
  // top like it should.
  useLayoutEffect(() => {
    if (typeof ref === "object" && ref?.current) {
      ref.current.scrollTop = 0;
    }
  }, [ref]);

  // Track the visual viewport so the popover can stay centered when
  // iOS' soft keyboard pops up. visualViewport.offsetTop/height
  // reflect the visible area shrinking from the bottom; pinning the
  // popover's `top` to offsetTop + margin keeps it inside the
  // visible slice instead of sliding behind the keyboard.
  const [vp, setVp] = useState(readVp);
  useEffect(() => {
    const v = typeof window !== "undefined" ? window.visualViewport : null;
    const update = () => setVp(readVp());
    v?.addEventListener("resize", update);
    v?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      v?.removeEventListener("resize", update);
      v?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const MARGIN = 12;
  const isMobile = vp.width < 640;

  // Anchor parameter is still accepted (used to be the cell rect we
  // hung off), but the popover is now centered on the viewport at
  // every breakpoint so the full code list is always reachable
  // without scrolling the card off-screen or cropping the bottom
  // of the menu. void it explicitly so the linter doesn't complain
  // about the prop being unused.
  void anchor;

  // Wrapper covers the visible viewport (visualViewport-aware so
  // iOS's keyboard shrinks it to the visible slice instead of the
  // full window). Flex centers the card inside that slice — mobile
  // pins to the top to keep the soft keyboard from shoving the
  // custom-code input behind itself.
  const width = Math.min(vp.width - 2 * MARGIN, 440);
  const maxH = Math.max(240, vp.height - 2 * MARGIN);

  const bulk = selectionSize > 1;
  return createPortal(
    <div
      // Full-viewport positioning wrapper. Flex centers the card
      // inside the visible viewport slice — using visualViewport
      // bounds so iOS's keyboard correctly shrinks the centering
      // area instead of leaving the card behind the keyboard.
      // Click on the empty space outside the card closes it.
      className="fixed z-[999] flex justify-center"
      style={{
        top: vp.offsetTop,
        left: vp.offsetLeft,
        width: vp.width,
        height: vp.height,
        padding: MARGIN,
        alignItems: isMobile ? "flex-start" : "center",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Soft backdrop tint behind everything in the wrapper so the
          schedule grid recedes while the picker is open. Sits in
          the same fixed layer at lower opacity than the click
          target above. */}
      <div className="fixed inset-0 -z-10 bg-black/30" aria-hidden />
      <div
        ref={ref}
        className="bg-white border border-slate-200 shadow-xl rounded-2xl overflow-auto"
        style={{ width, maxHeight: maxH }}
        role="dialog"
      >
      <div className="px-5 py-4 border-b border-slate-100 text-sm flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {bulk ? (
            <>
              <div className="font-semibold text-sky-700">
                Bulk edit — {selectionSize} cells selected
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Picking a code applies it to every selected cell.
              </div>
            </>
          ) : (
            <>
              <div className="font-medium text-slate-800 truncate">
                {employee.rank ? `${employee.rank}. ` : ""}{employee.lastName}, {employee.firstName}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{date}</div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 -mr-1.5 -mt-1"
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Cell notes: existing chips + inline input. Hidden in
          * bulk-edit mode since notes are per-(officer, date). Each
          * chip carries a × when the current user can delete it
          * (author OR L2+ supervisor). Enter or the Add button
          * submits — the input clears on success and the new note
          * shows up as a fresh chip. */}
        {!bulk && (
          <div className="space-y-2">
            {cellNotes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {cellNotes.map((n) => {
                  const canDelete = canDeleteAnyNote
                    || (currentUserId && n.createdBy === currentUserId);
                  return (
                    <span
                      key={n.id}
                      className="inline-flex items-start gap-1 px-2 py-1 rounded-md bg-sky-50 border border-sky-200 text-xs text-sky-900 max-w-full"
                      title={new Date(n.createdAt).toLocaleString()}
                    >
                      <span className="whitespace-pre-wrap break-words">
                        {n.note}
                      </span>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => onDeleteNote(n.id)}
                          className="text-sky-500 hover:text-sky-800 font-semibold leading-none mt-px"
                          aria-label="Remove note"
                          title="Remove note"
                        >×</button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitNote();
                  }
                }}
                placeholder="Add a note…"
                className="flex-1 rounded border-slate-300 text-sm px-2 py-1"
              />
              <button
                type="button"
                onClick={submitNote}
                disabled={!noteDraft.trim()}
                className="px-2.5 py-1 rounded bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white text-xs font-semibold"
              >
                Add
              </button>
            </div>
          </div>
        )}
        {/* Partial time-off info banner. Renders at the top of the
          * popover so the supervisor sees the approved partial-day
          * window and the requester's note before scanning shift
          * codes — mobile especially, where the cell tooltip on
          * hover doesn't exist. */}
        {partialTimeOff && partialTimeOff.startTime && partialTimeOff.endTime && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1 dark:bg-indigo-500/25 dark:border-indigo-700 dark:text-indigo-100">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-amber-200 text-amber-900 dark:bg-indigo-500/50 dark:text-indigo-100">
                {partialTimeOff.code}
              </span>
              <span className="font-semibold">
                Partial {partialTimeOff.typeLabel.toLowerCase()}
              </span>
              <span className="tabular-nums">
                {partialTimeOff.startTime}–{partialTimeOff.endTime}
              </span>
            </div>
            {partialTimeOff.note && (
              <div className="text-amber-900/80 whitespace-pre-wrap dark:text-indigo-100/80">
                {partialTimeOff.note}
              </div>
            )}
          </div>
        )}
        {pickerError && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 space-y-2">
            <div className="flex items-start gap-2">
              <span className="font-semibold mt-px">Fix and try again:</span>
              <span className="flex-1">{pickerError.message}</span>
              <button
                type="button"
                onClick={() => setPickerError(null)}
                className="text-red-400 hover:text-red-700 font-semibold leading-none"
                aria-label="Dismiss"
              >×</button>
            </div>
            {onForce && (
              <div className="flex items-center gap-2 pt-1 border-t border-red-200">
                <span className="text-red-700">
                  Not the right validation for this case?
                </span>
                <button
                  type="button"
                  onClick={submitForce}
                  className="ml-auto rounded bg-red-600 hover:bg-red-700 text-white font-semibold px-2 py-1"
                  title="Save the code anyway — the cell will render in bold red so it's obvious the guard was overridden. L2+ only."
                >
                  Force save anyway →
                </button>
              </div>
            )}
          </div>
        )}
        {existing ? (
          <button
            onClick={submitClear}
            className={
              "w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold border flex items-center gap-2 "
              + (isForcedOverride(existing)
                ? "bg-red-100 text-red-800 hover:bg-red-200 border-red-300"
                : "bg-red-50 text-red-700 hover:bg-red-100 border-red-200")
            }
            title={isForcedOverride(existing)
              ? "Revert this forced override — the row was manually saved past a validation error."
              : undefined}
          >
            <span className="inline-block w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] leading-none">×</span>
            {isForcedOverride(existing) ? "Revert override" : "Remove code"}
            <span className="ml-auto font-mono text-xs text-red-500">
              {(existing.note ?? "").trim().toUpperCase() || "—"}
            </span>
          </button>
        ) : timeOffCode ? (
          // Approved time-off overlay. NOT a remove button — that
          // used to hard-delete the underlying time_off record from
          // the schedule, which was surprising. The request is a
          // durable record with its own review + audit trail; if a
          // supervisor actually wants to cancel it, they use the
          // Time Off page. From here you can only STAMP a shift code
          // over the cell (below) to override the schedule display
          // without touching the request.
          <div className="px-3 py-2.5 rounded-lg text-sm font-medium bg-amber-50 text-amber-900 border border-amber-200 flex items-center gap-2 dark:bg-indigo-500/25 dark:border-indigo-700 dark:text-indigo-100">
            <span className="inline-block px-1 rounded bg-amber-200 text-amber-900 font-mono text-xs dark:bg-indigo-500/50 dark:text-indigo-100">
              {timeOffCode}
            </span>
            <span>Approved time off</span>
            <a
              href="#/time-off"
              className="ml-auto text-xs text-sky-700 hover:underline"
            >
              Manage on Time Off →
            </a>
          </div>
        ) : (
          <div className="px-3 py-2 text-sm text-emerald-700 font-medium border border-emerald-200 bg-emerald-50 rounded-lg flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-600" />
            Currently working
          </div>
        )}

        {/* Custom entry moved ABOVE the code list so autofocus on a
          * hardware keyboard doesn't scroll the popover to the
          * bottom to reveal the input — the input is now near the
          * top of the picker where the natural scroll position
          * keeps it visible without displacing the code list. */}
        <div className="border-t border-slate-100 pt-3">
          <label className="block px-1 pb-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Custom (letters → code, e.g. SCU · digits → 24h hours, e.g. 06-14)
          </label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (custom.trim()) submitPick(custom);
            }}
          >
            <input
              // Autofocus only when the device has a real (hardware)
              // keyboard. On touch devices, autofocus would pop the
              // soft keyboard the moment the picker opens — which
              // shoves the code list off-screen and forces iOS's
              // input-accessory toolbar over the bottom of the page.
              // The custom input is still tappable to focus when the
              // officer actually wants to type a custom code.
              autoFocus={hasHardwareKeyboard()}
              value={custom}
              onChange={(e) => { setCustom(e.target.value); if (pickerError) setPickerError(null); }}
              maxLength={8}
              className="flex-1 text-sm font-mono px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="e.g. 9-4"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={!custom.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40"
            >
              Set
            </button>
          </form>
        </div>

        {codes.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <div className="px-1 pb-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Off / leave codes
            </div>
            <div className="space-y-0.5">
            {codes.map((c) => {
              const activeNote = (existing?.note ?? timeOffCode ?? "")
                .trim().toUpperCase();
              const selected = activeNote === c.code.toUpperCase();
              return (
                <button
                  key={c.id}
                  onClick={() => submitPick(c.code)}
                  className={
                    "w-full text-left px-3 py-2 rounded-lg text-sm flex items-baseline gap-3 hover:bg-slate-100 " +
                    (selected ? "bg-sky-50" : "")
                  }
                >
                  <span className="font-mono font-semibold text-slate-800 w-12">{c.code}</span>
                  <span className="text-xs text-slate-500 truncate">{c.label}</span>
                  {selected && <span className="ml-auto text-xs text-sky-600">●</span>}
                </button>
              );
            })}
            </div>
          </div>
        )}

        {/* Cell audit link. Only rendered when the parent passed a
          * handler (which it only does for users with viewAudit).
          * Sits below the custom-code entry so it's out of the way
          * but always discoverable at the bottom of the picker. */}
        {onViewHistory && (
          <div className="border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={onViewHistory}
              className="w-full text-left px-1 py-1 text-xs text-slate-500 hover:text-slate-800 hover:underline flex items-center gap-1.5"
              title="See every change made to this cell in the audit log"
            >
              <span aria-hidden>🕒</span>
              View change history for this cell →
            </button>
          </div>
        )}
      </div>
    </div>
    </div>,
    document.body,
  );
});

// ----------------------------------------------------------------------
// InlineCellEditor — a small text input that overlays a single cell
// with a typeahead dropdown of shift codes. This is what fires when
// a supervisor double-clicks a cell: they can just type, see
// matching codes filter live, hit Enter to commit. Free-text custom
// values (e.g. "9-4", "124") also commit so off-roster codes still
// work without round-tripping through the popover.
//
// Keyboard model is intentionally spreadsheet-flavoured:
//   * Empty + Enter        → clear the cell
//   * Typed + Arrow + Enter → commit the highlighted suggestion
//   * Typed + Enter (no    → commit the typed text verbatim as a
//     highlight)             custom code
//   * Escape               → cancel without writing
//   * Click outside        → commit current text
//
// Filtering matches against both the code letters and the human
// label, case-insensitive, so typing "vac" surfaces "V — Vacation"
// just like typing "v" does.
function InlineCellEditor({
  anchor, codes, seed, onCommit, onClear, onClose,
}: {
  anchor: DOMRect;
  codes: ShiftCode[];
  seed: string;
  // Return-a-result callbacks: on failure the editor paints the
  // error just below the input and stays open so the supervisor
  // can fix and retry without reopening from the grid.
  onCommit: (value: string) => { ok: true } | { ok: false; error: string };
  onClear: () => { ok: true } | { ok: false; error: string };
  onClose: () => void;
}) {
  const [value, setValue] = useState(seed);
  const [highlight, setHighlight] = useState(0);
  const [editorError, setEditorError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return codes.slice(0, 24);
    // Rank: prefix match on code first, then prefix match on
    // label, then substring fallback. That way typing "v" puts
    // "V — Vacation" above "S — Sick" even if Sick's label
    // happens to contain a v somewhere.
    const score = (c: ShiftCode): number => {
      const code = c.code.toLowerCase();
      const label = c.label.toLowerCase();
      if (code === q) return 0;
      if (code.startsWith(q)) return 1;
      if (label.startsWith(q)) return 2;
      if (code.includes(q)) return 3;
      if (label.includes(q)) return 4;
      return 99;
    };
    return codes
      .map((c) => ({ c, s: score(c) }))
      .filter((x) => x.s < 99)
      .sort((a, b) => a.s - b.s || a.c.code.localeCompare(b.c.code))
      .map((x) => x.c)
      .slice(0, 24);
  }, [value, codes]);

  // Reset highlight to top whenever filtered list changes so the
  // current "top match" is what an immediate Enter would commit.
  useEffect(() => { setHighlight(0); }, [suggestions]);

  // Click outside the editor commits whatever's typed. Matches the
  // spreadsheet pattern — abandoning the cell is a save action.
  // If the commit fails validation, keep the editor open so the
  // supervisor sees + fixes the error in place.
  useEffect(() => {
    function away(ev: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) {
        const v = value.trim();
        if (v === seed.trim()) { onClose(); return; }
        const r = onCommit(v);
        if (!r.ok) { setEditorError(r.error); return; }
        onClose();
      }
    }
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [value, seed, onCommit, onClose]);

  useEffect(() => {
    // Select-all on mount so the supervisor can overwrite the
    // existing code immediately by typing, or accept it as-is with
    // Enter.
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Positioning: anchored to the cell, sized to it, dropdown
  // hangs below. Falls back above if there's no room beneath.
  const MARGIN = 6;
  const DROPDOWN_H = 220;
  const vpH = typeof window !== "undefined" ? window.innerHeight : 800;
  const placeBelow = anchor.bottom + DROPDOWN_H + MARGIN < vpH;
  // Offset of the dropdown's top edge relative to the outer
  // wrapper (which is pinned to the cell's top-left). Below: just
  // past the input's bottom. Above: lift it up by its own height.
  const dropdownTop = placeBelow
    ? anchor.height + 2
    : -DROPDOWN_H - 2;

  function commitHighlighted() {
    const v = value.trim();
    const r = v === ""
      ? onClear()
      : (suggestions[highlight]
        ? onCommit(suggestions[highlight].code)
        : onCommit(v));
    if (!r.ok) { setEditorError(r.error); return; }
    onClose();
  }

  return createPortal(
    <div
      ref={boxRef}
      className="fixed z-[1000]"
      style={{ left: anchor.left, top: anchor.top }}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => { setValue(e.target.value); if (editorError) setEditorError(null); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitHighlighted();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === "Tab") {
            // Tab acts like Enter — commit and let the surrounding
            // grid handle focus movement.
            e.preventDefault();
            commitHighlighted();
          }
        }}
        maxLength={16}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        className="font-mono uppercase text-center text-xs px-1 bg-white border-2 border-sky-500 outline-none rounded-sm shadow"
        style={{
          width: Math.max(anchor.width, 64),
          height: anchor.height,
        }}
      />
      {editorError && (
        <div
          className="absolute bg-red-50 border border-red-300 rounded-md shadow-xl px-3 py-2 text-xs text-red-800 flex items-start gap-2"
          style={{
            top: dropdownTop,
            left: 0,
            minWidth: Math.max(anchor.width, 260),
            maxWidth: 360,
          }}
        >
          <span className="font-semibold shrink-0">Try again:</span>
          <span className="flex-1">{editorError}</span>
        </div>
      )}
      <div
        className="absolute mt-0.5 bg-white border border-slate-200 rounded-md shadow-xl overflow-auto"
        style={{
          // When the error banner is showing, drop the suggestion
          // list below it so both are readable.
          top: editorError ? dropdownTop + 44 : dropdownTop,
          left: 0,
          minWidth: Math.max(anchor.width, 220),
          maxHeight: DROPDOWN_H,
        }}
      >
        {suggestions.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-500">
            No matching code — press Enter to save{" "}
            <span className="font-mono font-semibold">
              "{value.trim().toUpperCase()}"
            </span>{" "}
            as a custom note.
          </div>
        ) : (
          <ul className="py-1">
            {suggestions.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown beats the outside-click handler so
                    // the click actually commits this suggestion
                    // instead of saving the typed value.
                    e.preventDefault();
                    onCommit(c.code);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={
                    "w-full text-left px-3 py-1 text-sm flex items-baseline gap-2 "
                    + (i === highlight ? "bg-sky-50" : "hover:bg-slate-50")
                  }
                >
                  <span className="font-mono font-semibold text-slate-800 w-10">
                    {c.code}
                  </span>
                  <span className="text-xs text-slate-500 truncate">{c.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Inline "+ Add officer" button that opens a small dropdown of employees
// not currently rostered on this shift.
//
// The menu uses FIXED positioning (not absolute inside the button's
// relative wrapper) so it floats above the table regardless of sticky
// cells and stacking contexts created by sibling rows. Without this,
// the menu gets obscured by the next shift section's rows.
function AddOfficerButton({
  shift, addable, onPick,
}: {
  shift: Shift;
  addable: Employee[];
  onPick: (emp: Employee) => void;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!anchor) return;
    function away(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setAnchor(null);
    }
    function esc(e: KeyboardEvent) { if (e.key === "Escape") setAnchor(null); }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchor]);

  function toggle() {
    if (anchor) { setAnchor(null); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setAnchor(rect);
  }

  // Same flip-and-cap logic as the cell picker so a tap on the
  // "+ Add officer" button near the bottom of a phone viewport
  // doesn't render most of the menu off-screen.
  const MARGIN = 8;
  const MENU_W = 256;
  const MENU_DESIRED_H = 256;
  const spaceBelow = anchor ? window.innerHeight - anchor.bottom - MARGIN : 0;
  const spaceAbove = anchor ? anchor.top - MARGIN : 0;
  const placeBelow = spaceBelow >= 200 || spaceBelow >= spaceAbove;
  const menuMaxH = anchor
    ? Math.max(160, Math.min(MENU_DESIRED_H, placeBelow ? spaceBelow : spaceAbove))
    : MENU_DESIRED_H;
  const menuTop = anchor
    ? (placeBelow ? anchor.bottom + 4 : Math.max(MARGIN, anchor.top - 4 - menuMaxH))
    : 0;
  const menuLeft = anchor
    ? Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - MENU_W - MARGIN))
    : 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className="text-[11px] text-slate-500 hover:text-sky-700 hover:bg-sky-50 px-2 py-1 rounded border border-dashed border-slate-300"
        title={`Add an officer to ${shift.name}`}
      >
        + Add officer
      </button>
      {anchor && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[1000] w-64 overflow-auto bg-white border border-slate-200 shadow-xl rounded"
          style={{ top: menuTop, left: menuLeft, maxHeight: menuMaxH }}
        >
          {addable.length === 0 ? (
            <div className="p-3 text-xs text-slate-500 italic">
              No other active employees available.
            </div>
          ) : (
            addable.map((e) => (
              <button
                key={e.id}
                onClick={() => { onPick(e); setAnchor(null); }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <span className="font-medium text-slate-800">
                  {e.rank ? `${e.rank}. ` : ""}{e.lastName}, {e.firstName}
                </span>
                {(e.radioNumber ?? e.badgeNumber) && (
                  <span className="text-slate-400 ml-2">#{e.radioNumber ?? e.badgeNumber}</span>
                )}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

// ---- Helpers -----------------------------------------------------------

// Pay-period weeks to tint (set via Settings; defaults to [0, 2]).
let payWeekendWeeksForChrome: number[] = [0, 2];
export function setPayWeekendWeeksForChrome(v: number[]) {
  payWeekendWeeksForChrome = v;
}

function cellChrome(d: Date, today: Date, dayIndex: number, extra = "") {
  const isToday = isSameYmd(d, today);
  const day = d.getDay();
  // Pay-period markers. Each entry in payWeekendWeeksForChrome
  // identifies the START week of a biweekly pay period (0-indexed
  // against the 4-week schedule window). For every such week W we
  // mark two cells:
  //   * the START Sunday of the pay period — dayIndex = W*7
  //   * the END Saturday of the pay period — dayIndex = W*7 + 13
  //     (two weeks later, minus one to land on the closing Sat)
  // So picking weeks 1 + 3 (the default) lights up the four
  // bookend days of the two biweekly periods: Sun (1st), Sat
  // (end of week 2), Sun (start of week 3), Sat (end of week 4).
  let inPayWeekend = false;
  for (const w of payWeekendWeeksForChrome) {
    if (day === 0 && dayIndex === w * 7) { inPayWeekend = true; break; }
    if (day === 6 && dayIndex === w * 7 + 13) { inPayWeekend = true; break; }
  }
  return [
    // Pay-weekend bookend marker. Soft warm wash in light mode; in
    // dark mode we use violet — distinct from the indigo attention
    // wash and the sky hover highlight, so pay-period edges don't
    // look like either of those signals. amber-50 beneath would
    // otherwise bleed through warm and read as olive/brown on a
    // slate background.
    inPayWeekend ? "bg-amber-50 dark:bg-violet-500/25" : "",
    // Today's column gets the same full-cell overlay treatment as
    // the mouse-hover highlight, but green. cell-today is a single
    // class defined in styles.css (inset box-shadow) so it
    // composites over whatever background the cell carries.
    isToday ? "cell-today" : "",
    extra,
  ].filter(Boolean).join(" ");
}

// "HH:MM" + minutes → "HH:MM", wrapping around midnight.
function shiftEndTime(start: string, durationMin: number): string {
  const [h, m] = start.split(":").map((n) => Number(n));
  const total = ((h || 0) * 60 + (m || 0) + durationMin) % (24 * 60);
  const eh = Math.floor(total / 60);
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

// True only when the user-agent reports a fine pointer + hover —
// i.e. an actual mouse / trackpad with a hardware keyboard. Touch
// devices report (hover: none) and (pointer: coarse), even tablets
// with a stylus. Used to gate features that play badly with the
// soft keyboard, like autofocusing an input on popover open.
function hasHardwareKeyboard(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(hover: hover) and (pointer: fine)").matches
    ?? false;
}

// Snapshot the visible viewport — width/height of the area that is
// not covered by the on-screen keyboard, plus its offset within
// the layout viewport. Falls back to window.innerWidth/Height on
// older Safaris and during SSR. Subscribers re-read this on the
// VisualViewport's resize/scroll events to keep modal-style
// popovers centered when iOS shifts the page to bring a focused
// input into view.
function readVp(): {
  width: number; height: number; offsetTop: number; offsetLeft: number;
} {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, offsetTop: 0, offsetLeft: 0 };
  }
  const v = window.visualViewport;
  return {
    width: v?.width ?? window.innerWidth,
    height: v?.height ?? window.innerHeight,
    offsetTop: v?.offsetTop ?? 0,
    offsetLeft: v?.offsetLeft ?? 0,
  };
}

